export type Organization = {
  slug: string
  name: string
  timezone: string
}

export type Principal = {
  login: string
  display_name: string
  role_name: string | null
  permissions: string[]
  organization: Organization
  /** True whenever the panel cannot be framed, for any of the reasons below. */
  dispatch_pending: boolean
  /** WHY it cannot be framed, which is what the screen has to say:
   *
   *  off      - this customer has no dispatch panel connection switched on.
   *  no_seat  - the integration is live; this login has no seat assigned.
   *  pending  - there is a seat, waiting on a WalkieFleet account that is made
   *             by hand on the fleet server.
   *  ready    - it can be framed. */
  dispatch_state: 'off' | 'no_seat' | 'pending' | 'ready'
  dispatch_level: number | null
  /** Whether the radio card can be delivered at all (`broadcasting.CARD_DELIVERY`).
   *  Absent from a server older than this field, which reads as false. */
  card_delivery?: boolean
}

export type Dependency = {
  ok: boolean | null
  required: boolean
  note?: string
}

export type Status = {
  app: string
  environment: string
  dependencies: Record<string, Dependency>
  schema: { applied?: string[]; pending?: string[]; error?: string }
}

/** What this login may do, for hiding controls it cannot use.
 *
 *  THE SERVER IS THE BOUNDARY, NOT THIS.
 *
 *  Every gated route checks for itself; this only decides what to DRAW. A
 *  button that is rendered and then fails with a 403 is worse than one that is
 *  not there — especially mid-call, where the dispatcher has to work out
 *  whether he pressed the wrong thing or needs to ring somebody.
 *
 *  Mirrors auth.Principal.can exactly: flat membership with a "*" wildcard and
 *  no prefix logic. Granting `calls` does not grant `calls.active.actions.close`
 *  — the server stores every leaf explicitly for that reason. */
let held: string[] = []

export function rememberPermissions(permissions: string[]): void {
  held = permissions ?? []
}

export function can(permission: string): boolean {
  return held.includes('*') || held.includes(permission)
}

/** Whether the radio card exists yet. The SERVER decides and says so on the
 *  session; remembered beside the permissions for the same reason they are. */
let cardOn = false

export function rememberCardDelivery(on: boolean | undefined): void {
  cardOn = !!on
}

export function cardDelivery(): boolean {
  return cardOn
}

/** Does this role hold ANY leaf inside one section?
 *
 *  FOR DRAWING A DOOR, NEVER FOR OPENING ONE. `can()` is exact membership on
 *  purpose -- prefix matching is how a permission system starts granting
 *  things nobody ticked. This grants nothing: it answers "is there anything
 *  behind this button", which is a different question, and the only honest way
 *  to draw a nav item for a section made of several independent leaves.
 *
 *  IT EXISTS BECAUSE THE HAND-WRITTEN VERSION DRIFTED. The Search button
 *  listed five of the six search leaves and the Reports button two of the
 *  three, so a stores-only role got no Search button and a member-activity
 *  coordinator got no Reports button -- a permission that was granted and a
 *  feature that was never built look identical from the far side of a missing
 *  button. A list that has to be edited every time the tree grows will be
 *  forgotten every time the tree grows.
 *
 *  The bare BRANCH key is deliberately not accepted as a match: a leaf always
 *  stores its branch (`permissions.clean`), so counting it would make this
 *  true for every role in the section and answer the wrong question. */
export function canAny(branch: string): boolean {
  if (held.includes('*')) return true
  return held.some((k) => k.startsWith(branch + '.'))
}

/** A CSV comes back as bytes, not JSON, so it does not go through `request`.
 *  The browser turns it into a file — which is also why the export is a POST:
 *  a download URL would carry the range into two access logs. */
async function downloadCsv(
  path: string, body: Record<string, unknown>,
): Promise<Blob> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  })
  if (!res.ok) {
    throw new ApiError(res.status, `Export failed (${res.status}).`)
  }
  return res.blob()
}

/** One open call draft, as its card shows it (069). `caller_label` is a
 *  name, "anonymous caller", or the number's last four digits -- never the
 *  whole number. `held_by_label` is whoever has it open now, or null. */
export type CallDraft = {
  id: string
  version: number
  created_at: string
  updated_at: string
  created_by_label: string
  updated_by_label: string | null
  status: string
  nature_label: string | null
  location_label: string | null
  caller_label: string | null
  held_by_label: string | null
  held_by_me: boolean
}

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

/** The server's own sentence, whatever shape it arrived in.
 *
 *  FastAPI's OWN 422 IS A LIST, not a string — one object per field, each with
 *  `loc` and `msg`. Handed to `Error`, an array renders as "[object Object]",
 *  which is what a dispatcher saw beside the button when a callback number was
 *  too long: no field named, no length, nothing to act on. Routes on the call
 *  path answer in sentences of their own (`_clip_fields`, `_answers_within`),
 *  so this is the last resort — but it must still say which box. */
function detailOf(body: unknown, status: number): string {
  const detail = (body as { detail?: unknown } | null)?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail)) {
    const said = detail
      .map((item) => {
        const one = item as { loc?: unknown[]; msg?: unknown }
        const where = Array.isArray(one.loc) ? one.loc[one.loc.length - 1] : null
        const said = typeof one.msg === 'string' ? one.msg : ''
        return where && said ? `${String(where)}: ${said}` : said
      })
      .filter(Boolean)
    if (said.length) return said.join(' ')
  }
  return `Request failed (${status}).`
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      ...init,
      // NOT SET FOR FormData, AND THAT IS NOT A STYLE CHOICE. A multipart body
      // has a boundary string, the browser is the only thing that knows it,
      // and it writes the whole `multipart/form-data; boundary=...` header
      // itself -- but only if nothing has already set Content-Type. Forcing
      // `application/json` here leaves the server with a body it cannot split,
      // and FastAPI answers 422 saying the FILE IS MISSING, which sends you
      // looking at the upload rather than at this line.
      headers: init?.body instanceof FormData
        ? { ...(init?.headers ?? {}) }
        : { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
      credentials: 'same-origin',
    })
  } catch {
    // The server itself is unreachable. Say so plainly rather than showing a
    // credentials error, which sends the dispatcher hunting for a typo.
    throw new ApiError(0, 'Cannot reach the server.')
  }

  if (res.status === 204) return undefined as T

  const body = await res.json().catch(() => null)
  if (!res.ok) {
    throw new ApiError(res.status, detailOf(body, res.status))
  }
  return body as T
}

/** What the phone book's importer says about a file, before anything is
 *  written. `counts` is a bare map because the server counts by GROUPING over
 *  the outcomes -- render whatever keys arrive, including one nobody
 *  enumerated. A screen that reads three named counters is how a broadcast
 *  that reached everybody once rendered no outcome at all. */
export type ImportPreview = {
  file_sha256: string
  filename: string
  format: string
  headers: string[]
  has_headers: boolean
  /** target key -> the label to show for it. From the server, so the screen
   *  cannot offer a target the importer does not know -- one that is not in
   *  `places.PARTS` would be dropped silently. */
  targets: Record<string, string>
  mapping: Record<string, number>
  region: string | null
  rows_read: number
  blank_rows_excluded: number
  counts: Record<string, number>
  /** WHAT THE STATUS COLUMN WOULD DO, on the roster preview only. Absent when
   *  no row names a category at all.
   *
   *  The importer creates nothing: it matches the Status against the
   *  categories the roster already has, so `defined: 0` means every one of
   *  those rows lands in no category — worth saying before the button rather
   *  than as a page of problems afterwards. It does not refuse the file, which
   *  is why there is a count here and no `blocked` flag: the members are still
   *  added and their category is set on the record. */
  categories?: {
    /** How many categories this organization has. */
    defined: number
    /** Only those a row actually joins, by the server's own count. */
    joining: Array<{ name: string; rows: number }>
  }
  /** How many rows will actually be written. Decided by the server, because
   *  "create plus widen" is a second definition of it and was already wrong -
   *  a row whose number could not be read still creates the person. */
  will_write: number
  /** Which outcome keys count as writing something, so the dialog cannot drift
   *  from the importer's own list. */
  writes: string[]
  sample: Array<{
    row: number; outcome: string
    display_name: string; phone: string; address: string
  }>
  problems: ImportProblem[]
}

/** Row, column and reason. Never the value -- "never log a caller's phone
 *  number" covers a report on a screen and a row in a table, and the person
 *  reading this has the file open in front of them. */
export type ImportProblem = {
  row: number
  outcome: string
  column?: string
  reason?: string
  held_by?: string
  first_seen_row?: number
}

export type ImportResult = {
  batch_id: string
  rows_read: number
  contacts_created: number
  contacts_widened: number
  numbers_attached: number
  counts: Record<string, number>
  /** Non-null when a batch failed part-way. What came before it is written and
   *  stays written, deliberately. */
  stopped_at_row: number | null
  problems: ImportProblem[]
}

/** A past import, and what taking it back would cost. */
export type ImportBatch = {
  id: string
  filename: string | null
  row_count: number | null
  imported_count: number | null
  skipped_count: number | null
  created_at: string
  imported_by_name: string | null
  undone_at: string | null
  undo_kept: number | null
  contacts_still_present: number
  undo?: {
    contacts: number
    widened: number
    numbers: number
    calls: number
    incidents: number
    /** Numbers a dispatcher typed onto an imported record AFTER the import.
     *  The one thing an undo destroys that it did not create -- contact_phones
     *  is ON DELETE CASCADE. Said out loud before the button is pressed. */
    hand_added_numbers: number
    /** Imported records somebody has edited since. */
    edited_since: number
  }
}

export type ImportUndone = {
  batch_id: string
  contacts_deleted: number
  numbers_removed: number
  contacts_restored: number
  calls_unclaimed: number
  incidents_unclaimed: number
  /** Left as it was, because somebody changed it after the import wrote it.
   *  An undo that forced these would be a worse failure than the import. */
  left_alone: number
  hand_added_numbers_lost: number
}

/** The roster import's result. Its own type because the roster counts
 *  members and has no undo -- see app/routes/member_import.py for why. */
export type MemberImportResult = {
  batch_id: string
  rows_read: number
  members_created: number
  members_widened: number
  /** NUMBERS, NOT MEMBERS, and the summary has to say both. Re-importing a
   *  corrected file — which the dialog tells the operator to do — is
   *  overwhelmingly a WIDEN that attaches second and third numbers, so a
   *  report counting only members reads "0 added, 24 already on the roster"
   *  for an upload whose entire purpose was the numbers. */
  numbers_attached: number
  /** Already on that member, so not added again. Counted rather than treated
   *  as a fault: re-uploading the same file must be safe and quiet. */
  numbers_already_there: number
  counts: Record<string, number>
  stopped_at_row: number | null
  problems: ImportProblem[]
}

/** One login of one organization.
 *
 *  NOTE WHAT IS NOT HERE: no password and no hash. A hash is still a
 *  credential, and a screen holding one can leak it into a log, a bug report
 *  or a browser's memory for nothing in return. Passwords go one way only. */
export type OrgUser = {
  id: string
  login: string
  display_name: string
  email: string | null
  phone: string | null
  role_id: string | null
  role_name: string | null
  disabled: boolean
  last_seen_at: string | null
  created_at: string
  /** The WalkieFleet account cannot be created by any API -- it is made by
   *  hand on the fleet server. Until it exists this dispatcher is PENDING and
   *  must be shown as such rather than framed onto nothing. */
  fleet_account_ready: boolean
  panel_linked: boolean
}

export type ListName = 'natures' | 'departments' | 'resolutions' | 'relationships'

export type ListItem = {
  id: string
  name: string
  sort_order: number
  /** A timestamp, not a flag, so the record says when it stopped being used.
   *  Entries are retired and never deleted -- an incident from last year must
   *  still read correctly. */
  retired_at: string | null
  department_id?: string | null
  default_priority?: number | null
  fills_caller_address?: boolean | null
  /** Natures only: the short code a dispatcher says over the radio, FT for
   *  flat tire. Capitals, as the server stored them; null for none. Absent on
   *  the other three lists, and never in the broadcast. */
  code?: string | null
}

/** How a question is answered: picked off its own list, typed, or a vehicle.
 *
 *  A `vehicle` is make, model, year and colour, each a dropdown over the
 *  server's vehicle catalogue that also takes typing (`lib/vehicles.tsx`). It
 *  has no options of its own, adds nothing to the call code and cannot be a
 *  safety answer, but it may go out in the broadcast -- migration 049. */
export type QuestionKind = 'choice' | 'text' | 'vehicle'

/** One question on a nature of call's conditional tree.
 *
 *  THE CUSTOMER'S OWN DESIGN: "the dispatcher first selects the Nature of Call,
 *  and only the questions relevant to that call type and previous answers
 *  should appear."
 *
 *  `key` IS THE IDENTITY AND NEVER CHANGES. It is what every stored answer is
 *  filed under and what every condition points at, so the server refuses to
 *  change one -- rewording "Where is the vehicle?" must not orphan the
 *  conditions hanging off it or rewrite the answers frozen on last month's
 *  jobs. It is generated from the prompt by `call_questions._slug` the first
 *  time the question is written, and NOTHING in the browser derives one: a
 *  second slug rule here would disagree with the server's the first time two
 *  questions were worded the same (`_free_key` nudges the second to `..._2`).
 *
 *  `sort_order` MEANS TWO DIFFERENT THINGS AND THAT IS THE SERVER'S DOING.
 *  Inside a nature's TREE it is the RESOLVED position in that tree, which is
 *  what `incident_answers.sort_order` stores and what a ticket reads in; in the
 *  catalogue -- a set's own list -- it is the position within the set. Sort by
 *  it inside the list it came from and nowhere else. */
export type CallQuestion = {
  id: string
  key: string
  prompt: string
  kind: QuestionKind
  /** On a call with several natures (076): which of them, by position, this
   *  question is asked for -- the part of the call code its piece goes into. */
  owner?: number
  /** Null for a typed answer. At least two for a multiple-choice one -- the
   *  server refuses one option, because one option is not a question. */
  options: string[] | null
  /** What each option adds to the call code, BY POSITION: the piece at N
   *  belongs to the option at N, and "" adds nothing. Null when no option on
   *  this question adds anything. `callCode` in lib/questions.tsx reads it. */
  option_codes: string[] | null
  sort_order: number
  /** The condition: the KEY of the question whose answer decides whether this
   *  one is asked, and which of its answers count. Both halves or neither.
   *
   *  IT NAMES A QUESTION, NEVER A NATURE, and that is the whole mechanism of
   *  the shared set: Flat tire's "which side is the flat on" hangs off the
   *  SHARED street question without forking a copy of it, and Boost simply
   *  does not include the tire question. */
  ask_when_key: string | null
  ask_when_values: string[] | null
  /** THE OTHER KIND OF CONDITION (077): asked only when the caller is NOT
   *  staying on scene -- "Where will the keys be?". Never with `ask_when_key`. */
  ask_when_caller_leaves?: boolean
  /** Which answers are the alarm — red on the call and the queue — and whether
   *  they take the call to the top of the queue. Nothing is hardcoded to the
   *  word "child". More than one since migration 051 ("No" and "Not sure"). */
  alert_values: string[] | null
  raises_priority: boolean
  /** Whether EVERY answer rides out with the broadcast, as "prompt: answer".
   *  Off by default and ticked one question at a time: the broadcast is
   *  nature, location and urgency, just enough to decide whether to take it. */
  in_broadcast: boolean
  /** Whether the answer goes to the member who accepts, as "prompt: answer".
   *  ON by default, the reverse of `in_broadcast`: the acceptor has always
   *  been sent every answer, and this is the tick that keeps one back. */
  in_details: boolean
  /** How the answer reads in the message to the member going (084):
   *  "Flat" -> "Flat: Front left". Null: the full question. */
  short_word?: string | null
  /** BY POSITION, like `option_codes`: a note for the responder for each
   *  answer, "" for none. Null when no answer has one. */
  option_notes: string[] | null
  /** BY POSITION: the line the text message adds when that answer is given,
   *  "" for an answer that sends nothing. Null when none does. A question has
   *  this or `in_broadcast`, never both. */
  option_broadcasts: string[] | null
  /** Show it red as "not confirmed" on the working call while it is asked and
   *  unanswered. Refuses nothing. */
  expect_answer: boolean
  /** More than one answer may be picked (076): "which tires are flat". The
   *  answer then travels as a JSON list -- see `lib/questions.tsx`. */
  multiple?: boolean
  /** Exactly one of these. A question belongs either to a shared set or to one
   *  nature of call — the server makes that a CHECK. */
  set_id: string | null
  nature_id: string | null
  retired_at: string | null
  created_at: string
  /** Only inside a nature's TREE, and only on a question that came out of a
   *  shared set. Absent on the catalogue reads. */
  from_set_id?: string | null
  from_set_name?: string | null
}

/** A reusable bundle of questions — "Vehicle location" is the customer's own
 *  example, and it is one row that three natures point at rather than three
 *  copies that drift.
 *
 *  `questions` and `natures` ARE COUNTS HERE AND LISTS ON `QuestionSetDetail`.
 *  That is the server's shape rather than a choice made in the browser:
 *  `call_questions.set_detail` spreads the row and then overwrites both names
 *  with the real lists. Reading one as the other renders `[object Object]` in
 *  place of a number, so the two shapes are separate types and neither extends
 *  the other. */
export type QuestionSet = {
  id: string
  name: string
  sort_order: number
  retired_at: string | null
  created_at: string
  /** Live questions in it. */
  questions: number
  /** How many natures would stop asking them — what somebody wants to know
   *  before pressing Retire. */
  natures: number
}

export type QuestionSetDetail = Omit<QuestionSet, 'questions' | 'natures'> & {
  questions: CallQuestion[]
  natures: Array<{ id: string; name: string }>
}

/** One row of a nature's sequence: either one of its own questions or a whole
 *  shared set, in ONE order. That single order is the entire reason
 *  `nature_items` exists — two lists somebody has to interleave would be
 *  interleaved differently by the editor, the dispatcher's form and every
 *  report.
 *
 *  Everything below `created_at` comes from a LEFT JOIN, so exactly one of the
 *  two halves is filled in on any given row. */
export type NatureItem = {
  id: string
  sort_order: number
  question_id: string | null
  set_id: string | null
  created_at: string
  key: string | null
  prompt: string | null
  kind: QuestionKind | null
  /** Set when the nature's own question has been retired. The ITEM ROW STAYS —
   *  it is the question's position in this sequence, and deleting it would make
   *  a restored question invisible on every tree. */
  question_retired_at: string | null
  set_name: string | null
  set_retired_at: string | null
  set_questions: number | null
}

/** The editor's whole picture of one nature, and every nature-scoped write
 *  answers with it — so the screen never has to work out for itself what a
 *  reorder or a removal did. */
export type NatureQuestions = {
  nature_id: string
  items: NatureItem[]
  /** The same list with the sets expanded: what a dispatcher is actually
   *  asked. */
  tree: CallQuestion[]
  /** Questions on this nature that can NEVER be asked, because the question
   *  their condition names is not asked earlier on this nature. Silent and
   *  total otherwise: the question stops appearing on every call, for ever,
   *  with nothing on any screen to say so. */
  unreachable: CallQuestion[]
}

/** The text a broadcast would carry, composed BY THE SERVER.
 *
 *  NOTHING IN THE BROWSER MAY BUILD THIS STRING. `incidents.build_broadcast_body`
 *  is the only place a broadcast is composed and `tools/site_notes_test.py`
 *  asserts on its signature so that widening it is a deliberate act — a copy
 *  here would be a second composer, free to drift, and it would drift in the
 *  least visible direction there is: the settings screen would go on showing
 *  the message the product used to send.
 *
 *  `characters` COMES FROM THE SERVER TOO, and not from `body.length`. Python
 *  counts code points and JavaScript counts UTF-16 units, so one emoji pasted
 *  into a prompt would give two different figures for one message — and the
 *  figure is the only reason to show any of this. */
export type BroadcastPreview = {
  nature_id: string
  body: string
  characters: number
  /** The ticked answers that went in, in the order the composer put them.
   *  Empty is the ordinary case and NOT an error: the broadcast is lean, so
   *  `in_broadcast` is off on every question until somebody decides one answer
   *  changes whether a member takes the call. */
  answers: Array<{
    question_key: string; prompt: string; answer: string
    /** The exact line the composer added for it, and all it was given:
     *  "prompt: answer", an answer's own wording, or a vehicle without its
     *  plate. `answer` is here for the screen, and a vehicle's has the plate. */
    broadcast_text: string
  }>
  /** The organization's standing wording round the call (migration 053), and
   *  `whole` as a member would receive it. `characters` counts `whole`,
   *  because a header is exactly what pushes a text past the 1,600 where it
   *  is cut. */
  header: string | null
  footer: string | null
  whole: string
  /** The three invented values, returned so the screen labels what the
   *  composer was actually handed. Repeating them in the browser would let the
   *  label describe a message nobody composed. `urgency` is empty at the
   *  stand-in priority, which is what makes `characters` a floor: a call that
   *  goes out CRITICAL carries that word in front of all of this. */
  stand_ins: {
    location_label: string
    priority: number
    urgency: string
  }
}

/** How many questions each live nature asks. One small row per nature, read
 *  when the intake form opens — the trees themselves are fetched one at a
 *  time, because every tree on every load is the organization's whole question
 *  catalogue in every dispatcher's browser for the length of every shift. */
export type NatureQuestionCount = {
  nature_id: string
  name: string
  questions: number
}

/** What a question is created or changed with.
 *
 *  ONLY THE KEYS PRESENT ARE SENT, and that matters on the patch: the server
 *  reads `model_fields_set`, so `alert_values: null` means "no answer is red
 *  any more" and a missing `alert_values` means "leave it alone". Building
 *  one of these with `undefined` in a slot is how a field silently stops being
 *  saved — `JSON.stringify` drops it. */
export type QuestionInput = {
  prompt: string
  kind?: QuestionKind
  options?: string[] | null
  /** Parallel to `options`, the same length. On a patch the server refuses
   *  changed options without their pieces, so the two are always sent
   *  together; null clears every piece. */
  option_codes?: string[] | null
  sort_order?: number
  ask_when_key?: string | null
  ask_when_values?: string[] | null
  alert_values?: string[] | null
  raises_priority?: boolean
  in_broadcast?: boolean
  in_details?: boolean
  /** Parallel to `options`, like `option_codes`, and sent with it. */
  option_notes?: string[] | null
  option_broadcasts?: string[] | null
  expect_answer?: boolean
  /** More than one answer may be picked (076): choice questions only. */
  multiple?: boolean
  ask_when_caller_leaves?: boolean
  /** The short word (084); "" or null is none. */
  short_word?: string | null
}

/** `key`, `kind`, `set_id` and `nature_id` are absent on purpose — the server
 *  REFUSES them with a sentence rather than ignoring them, and a screen that
 *  said it had saved a rename that did not happen is the failure the frozen key
 *  exists to prevent. */
export type QuestionPatch = Partial<Omit<QuestionInput, 'kind'>> & {
  /** Retired, never deleted. A retired question keeps its row so that its key
   *  stays taken — freeing it would let a later question inherit the meaning of
   *  every answer already filed under it. */
  retired?: boolean
}

/** One answer, frozen onto the job: the prompt AS ASKED and the answer AS
 *  CHOSEN, copied rather than referenced.
 *
 *  NEVER RENDERED BY JOINING TO THE LIVE CATALOGUE. The customer will edit
 *  these questions constantly and a job from three months ago has to keep
 *  reading correctly — `incidents.nature_label`'s rule one level down. */
export type IncidentAnswer = {
  id: string
  /** For the editor, and never for rendering: the question may since have been
   *  reworded, retired, or taken off this nature. */
  question_id: string | null
  question_key: string
  prompt: string
  answer: string
  is_alert: boolean
  in_broadcast: boolean
  /** Frozen with it: whether this answer goes to the member who accepts. */
  in_details: boolean
  sort_order: number
  answered_at: string
  answered_by_user_id: string | null
  /** The piece this answer added to the call code, frozen when it was given;
   *  "" when it added none. */
  code_piece: string
  /** Every answer picked, on a question that took more than one (076); null
   *  otherwise. What the working call is seeded from -- `answer` is the
   *  sentence for the ticket. */
  answer_values?: string[] | null
  /** An answer to a vehicle question, in parts, frozen like the rest. Null on
   *  every other answer, and for a part nobody gave. `answer` holds the same
   *  vehicle as the sentence the ticket prints; these are what the working
   *  call puts back into the four boxes. */
  vehicle_make: string | null
  vehicle_model: string | null
  vehicle_year: number | null
  vehicle_colour: string | null
  vehicle_plate_number: string | null
  vehicle_plate_country: 'Canada' | 'United States' | null
  vehicle_plate_region: string | null
  /** The note for the responder that went with this answer, frozen. */
  responder_note: string | null
  /** The line a broadcast adds for it, frozen; null exactly when the answer is
   *  not in the broadcast (`incident_answers_broadcast_line`). */
  broadcast_text: string | null
}

/** What a save of the answers did. Returned by the answer route and, with
 *  `refused` filled in, by opening a call. */
export type AnswerOutcome = {
  stored: number
  cleared: number
  /** Keys this save could not accept — a question this nature no longer asks,
   *  or a choice an administrator has just withdrawn. Dropped and NAMED at
   *  intake rather than refused, because nothing may stop a call being opened;
   *  refused outright on the answer form, where there is a box to say it
   *  beside and losing the typing in silence is the worse failure. */
  dropped: string[]
  /** Answers stored differently from what was sent, or not stored, whose
   *  reason is NOT a stale screen -- each with the sentence to show. A vehicle
   *  stored without a year that was not a year; at intake, a vehicle that
   *  could not be read at all. A key here is never a stale drop. Optional so a
   *  server older than this field reads as having nothing to say. */
  notes?: Record<string, string>
  visible: string[]
  outstanding: number
  /** Whether this CALL is flagged, not whether this save flagged it. */
  alert: boolean
  priority_raised: boolean
  /** An alarm answer fired and the urgency was left where the dispatcher put
   *  it. The thread records it either way; this is so the screen can say so. */
  priority_held: boolean
  /** Only when opening a call: the answers could not be stored at all, and the
   *  call was opened anyway. */
  refused?: string | null
  /** The call code as stored after this save, or null when the nature has no
   *  code. */
  call_code: string | null
}

/** One of the numbers a member answers on. */
export type MemberPhone = {
  id: string
  /** As it was typed. The normalised twin stays on the server — it is what the
   *  send path and the caller-ID pop match against, and it has no business on
   *  a screen. */
  phone: string
  label: string | null
  is_primary: boolean
  /** WHETHER A BROADCAST TEXTS THIS NUMBER. The customer's own design: "it
   *  should be optional on his contact card, which one should have a check
   *  next to it that it should get the text." The member-level `notify_sms`
   *  is derived from these and is not separately settable.
   *
   *  ONE CONTROL WRITES IT — `TextTicks` on the record's Broadcast tab, through
   *  `memberPhoneUpdate` below. The numbers are added, corrected, re-primaried
   *  and removed on Personal information, and deliberately carry no tick there:
   *  the customer separated the two, "the numbers that are saved on his
   *  personal information should be available here", and two controls for one
   *  fact is the shape this codebase calls the phone-book bug. */
  notify: boolean
  /** Whether it could be read as a phone number at all. False means a
   *  broadcast cannot text it whatever the tick says — the server refuses the
   *  two together — so this is what the record screen goes amber on. */
  readable: boolean
}

export type MemberGroup = {
  id: string
  name: string
  /** TWO SEPARATE THINGS, which is the customer's own correction: a CATEGORY
   *  is what a member is and he holds at most one of them — the server refuses
   *  a second, and migration 046 refuses it under that. An AREA is where he
   *  covers, and he holds as many as apply. A LIST (066) is "your own
   *  groups" on the Messaging tab: a group made to message as one. */
  kind: 'category' | 'area' | 'list'
  active?: boolean
  sort_order?: number
  /** Active members in it. Counted on the server so the number beside a group
   *  and the people it would actually reach cannot disagree. */
  members?: number
}

/** One qualification the organization recognises — the catalogue row.
 *
 *  READ WITHOUT A KEY, WRITTEN WITH `settings.members`, the same split
 *  `member_groups` has and for the reason `app/routes/member_certifications.py`
 *  gives: every name on this list already rides out on the ungated roster
 *  listing, so a gate over the read would guard nothing while looking as though
 *  it did.
 *
 *  RETIRED, NEVER DELETED, so there is no delete route to call. A member who
 *  earned something keeps reading as having earned it after the organization
 *  stops issuing it, and the foreign key is ON DELETE RESTRICT besides. */
export type Certification = {
  id: string
  name: string
  active: boolean
  sort_order: number
  created_at: string
  /** ACTIVE members holding it, counted on the server. A number beside a
   *  qualification that included people who have left would say the
   *  organization is covered when it is not. */
  holders: number
}

/** One qualification held by one member, AS THE ROSTER LISTING CARRIES IT.
 *
 *  `expired` IS THE SERVER'S ANSWER AND IS NEVER RECOMPUTED HERE.
 *  `member_certifications.EXPIRED_SQL` decides it beside the row, against
 *  today in the ORGANIZATION's timezone — a browser comparing `expires_on`
 *  against its own clock would be a second definition of "lapsed", and the
 *  escalation queue already paid for that once when the front end recomputed a
 *  window and a coordinator who set five minutes got the hard-coded seven.
 *
 *  THIS IS THE COMPACT HALF. `note` is where a certificate number is typed, so
 *  it stays off the read every dispatcher's browser makes on every shift. */
export type MemberCertification = {
  id: string
  certification_id: string
  name: string
  /** A bare date — render with `calendarDate`, never `day`. Null means it does
   *  not expire, which is not the same as "we do not know". */
  expires_on: string | null
  expired: boolean
}

/** The same holding as the RECORD carries it — a strict superset, so anything
 *  written against the listing shape renders one of these unchanged.
 *
 *  It arrives on `GET /api/members/{id}` and as the whole body of all three
 *  holding writes. There is deliberately no `GET .../certifications` of its
 *  own: a read with no caller is the shape this repository keeps paying for. */
export type MemberCertificationRecord = MemberCertification & {
  obtained_on: string | null
  note: string | null
  /** Null when it does not expire, 0 on the last valid day, negative once
   *  `expired` is true. From the same `_TODAY` as `expired`, so the countdown
   *  and the flag cannot come to disagree about which day it is. */
  days_left: number | null
  /** Whether the organization still issues it. He keeps it either way — that
   *  asymmetry is the whole point of retiring rather than deleting. */
  certification_active: boolean
  created_at: string
}

export type Member = {
  id: string
  /** NULLABLE, because the unit number is the identity and half a real roster
   *  is units with nobody filled in yet. Use `label`, never this. */
  display_name: string | null
  callsign: string | null
  /** What to call him: the name, or the unit number when there is no name.
   *  Computed on the server so no screen invents its own fallback and a row
   *  can never render blank. */
  label: string
  /** The primary number, as typed. Derived from `phones` — not settable. */
  phone: string | null
  phones: MemberPhone[]
  email: string | null
  groups: MemberGroup[]
  /** Derived: true when any number is ticked. Not settable; the tick lives on
   *  the number. */
  notify_sms: boolean
  notify_email: boolean
  notify_card: boolean
  /** Whether he carries a handset. A CHANNEL, not decoration: most of a real
   *  roster has a radio and no phone and no email, and those members are
   *  reached on the air rather than being unreachable. */
  has_radio: boolean
  radio_imei: string | null
  /** His own car. Not a fleet vehicle — Traccar owns those. */
  vehicle_plate: string | null
  on_duty: boolean
  active: boolean
  joined_on: string | null
  created_at: string
  /** WHERE A LETTER GOES. The customer's own reason: "we should be able to
   *  send out letters to all our members", "send a gift for the holidays".
   *
   *  COLUMNS ON THE MEMBER, NOT A `places` ROW, and the same eight names the
   *  caller record uses so `AddressFields` is reused rather than copied. A
   *  `places` row is a DISPATCH location — it carries entry codes, cross
   *  streets and site notes, it is deduplicated by address, and it is what the
   *  dispatcher's address search reads. Filing a member's home there would put
   *  his house in that search, and would merge it with an incident location the
   *  day somebody is dispatched to his street: his home record would acquire
   *  notes about a call, and the call would acquire his home as its place.
   *
   *  AND IT IS NEVER GEOCODED. Every address a dispatcher types goes to
   *  nominatim.openstreetmap.org; a member's home is not a dispatch location
   *  and appears on no map, so sending the roster's home addresses to a third
   *  party would be a privacy cost with no feature behind it. */
  house_number: string | null
  street: string | null
  unit: string | null
  building: string | null
  city: string | null
  region: string | null
  postal_code: string | null
  country: string | null
  /** Whether the PRIMARY number could be read as a phone number at all. A
   *  member whose number did not parse looked fine everywhere and was
   *  discovered mid-emergency as a delivery marked failed. */
  phone_readable?: boolean
  /** WHAT HE IS TRAINED TO DO, and it rides on the UNGATED listing on purpose.
   *  The mailing address, the date of birth and the emergency contact went the
   *  other way (see `MemberRecord`): none of them answers a question the
   *  dispatcher's responder picker asks, and who is qualified is exactly what
   *  it asks. */
  certifications: MemberCertification[]
}

/** One member's WHOLE record, from `GET /api/members/{id}` and from every
 *  write that answers with him.
 *
 *  THE EXTRA FIELDS ARE A SEPARATE TYPE BECAUSE THEY ARE A SEPARATE READ, and
 *  that split is the gate rather than a convenience. `GET /api/members` is
 *  deliberately ungated — `Dispatch.tsx` calls it on load for the responder
 *  picker — so a home address or a date of birth riding on it would sit in
 *  every dispatcher's browser for the length of every shift, invisibly, because
 *  nothing on that screen draws it. A READ THAT HANDS OVER WHAT A WRITE IS
 *  GATED ON IS THE GATE. Every route that answers with this shape carries
 *  `settings.members`.
 *
 *  A `MemberRecord` IS A `Member`, so anything drawing a roster row draws one
 *  of these unchanged — including `certifications`, whose record shape is a
 *  superset of the listing's. */
export type MemberRecord = Member & {
  certifications: MemberCertificationRecord[]
  /** A bare date, like `joined_on` — `calendarDay`/`calendarDate`, never `day`,
   *  or a birthday renders a day early everywhere west of Greenwich and the
   *  person it belongs to notices. */
  date_of_birth: string | null
  /** WHO TO RING IF SOMETHING HAPPENS TO HIM. She is not a member of this
   *  organization and never agreed to be in its systems, so the smallest number
   *  of people who can read her name and number is the right number. */
  emergency_contact_name: string | null
  emergency_contact_relationship: string | null
  /** As it was typed. The normalised twin never leaves the server, exactly as
   *  `phone_e164` does not — nothing sends to this number and nothing matches
   *  against it, so a second copy of it would have no reader. */
  emergency_contact_phone: string | null
  /** Whether that number could be read as a phone number at all. It costs
   *  nothing tonight and everything on the night it is dialled, which is the
   *  one night nobody will be checking the roster screen — so a typo is worth
   *  showing while somebody is looking at the record and able to fix it. */
  emergency_contact_readable: boolean
}

/** A scan filed against a member — the identity card the customer asked to be
 *  able to hold, or anything else that was handed in.
 *
 *  NEITHER THE CONTENT NOR THE HASH IS HERE, and neither is an oversight. The
 *  bytes because a member with four scans would ship the whole filing cabinet
 *  in order to draw a table of filenames. The sha256 because it is a
 *  fingerprint: it lets somebody who already holds a copy of a document
 *  confirm it is the same one, without ever having been granted the document.
 *
 *  ONE SHAPE FROM ALL THREE JSON ROUTES — the listing, the upload and the
 *  delete all answer with this — so the screen never reconstructs what it just
 *  removed from state it has already re-rendered.
 *
 *  Reading one is an EVENT on the server: `member_documents.fetch` writes
 *  `audit_log` in the same transaction as the decryption, so there is no path
 *  to a document that was opened and not recorded. Drawing this list is
 *  deliberately not audited — it runs on every open of a record, and a hundred
 *  rows a day would bury the one afternoon somebody opened a passport. */
export type MemberDocument = {
  id: string
  member_id: string
  kind: 'identification' | 'other'
  filename: string
  /** DECIDED FROM THE MAGIC BYTES on the server, never from the browser's
   *  Content-Type — that value is supplied by whatever is uploading, and a
   *  ".jpg" whose contents are HTML is the oldest stored-XSS trick there is. */
  mime: 'image/jpeg' | 'image/png' | 'application/pdf'
  byte_size: number
  note: string | null
  uploaded_by_user_id: string | null
  /** The copy that outlives the login. `uploaded_by_user_id` is ON DELETE SET
   *  NULL, and a document whose provenance reads as nothing is a document
   *  nobody can account for — so this is what a screen shows. */
  uploaded_by_label: string | null
  uploaded_at: string
}

export type ChannelWrapper = {
  channel: 'sms' | 'email'
  enabled: boolean
  /** null means "use the organization's"; "" means this channel sends none.
   *  Two different instructions — see migration 053. */
  broadcast_header: string | null
  broadcast_footer: string | null
}

/** The spot a dispatcher pinned for one call, and who pinned it.
 *
 *  NOT the same thing as the place's own coordinates, which are the map's
 *  answer about the address. This is a person saying "go to the loading bay
 *  behind it", on this call only — so it is drawn with its own caption and
 *  never folded into the place. */
export type PinnedSpot = {
  latitude: number
  longitude: number
  at: string | null
  by: string | null
}

/** The combined rebroadcast's settings (073): Settings, Messages. */
/** Everything on the Messages pages as it is being typed (084, and the
 *  pages of 1 Nov 2026): the server draws every phone from it. */
export type MessagesPreviewIn = {
  template: string; thanks: string; thanks_on_accept: boolean; closed_template: string
  thanks_on_every?: boolean
  header?: string; footer?: string
  channels?: Record<string, { broadcast_header: string | null; broadcast_footer: string | null }>
  wrapped?: boolean
  switches?: Record<string, boolean>
  combined?: CombinedSettings
}
export type MessagesPreview = {
  samples: { key: string; name: string; text: string }[]
  closed: string; unknown: string[]; words: string[]
  /** Present when the pages' fields were sent. */
  broadcast?: Record<'sms' | 'email', { first: string; again: string }>
  switches?: { key: string; name: string; text: string }[]
  note?: string
  removed?: string
  /** Null when every invented call is "left out". */
  combined?: string | null
}

export type CombinedSettings = {
  lines: { text: string; urgent: boolean }[]
  /** Which button a call starts with, by how many are going; null = left out. */
  starts: { none: number | null; one: number | null; more: number | null }
  parts: { where: boolean; details: boolean; code: boolean; urgency: boolean
           open: boolean; going: boolean }
  /** "{n}" becomes the number of calls. */
  heading: string
  /** Where each call's button words go; its number stays on the call's line. */
  line_place: 'under' | 'above'
}

/** One open call in the Rebroadcast window (073). */
export type CombinedCall = {
  id: string
  number: string
  nature_label: string
  call_code: string | null
  called_at: string
  going: number
  /** How the call's own broadcast wrote where it is. */
  where: string
  /** The button it starts with; null = left out. */
  start: number | null
}

export type OrgSettings = {
  /** Wrapped round every broadcast on every channel. null sends none. */
  broadcast_header: string | null
  broadcast_footer: string | null
  /** What the member who accepts is sent (057): one switch per fixed line of
   *  the details message. The answers are chosen per question. */
  handover_address: boolean
  handover_cross_streets: boolean
  handover_caller_name: boolean
  handover_responder_notes: boolean
  /** 058: the call code instead of the nature in words, a map link after
   *  the address, and whether the header and footer wrap this message. */
  handover_code: boolean
  handover_map_link: boolean
  handover_wrapped: boolean
  /** 060: the note on the call's location goes to the acceptor as "Directions:". */
  handover_location_note: boolean
  /** 080: a member taken off a call is told -- only if he had been sent its
   *  details. Off by default. */
  tell_removed_member: boolean
  /** 084: the message to the member going in the organization's own words,
   *  with blanks; null = the message from the switches, as before. */
  member_template: string | null
  /** The thank-you line, and where it goes. */
  member_thanks: string | null
  thanks_on_accept: boolean
  thanks_on_every: boolean
  /** A message of its own to the members who went when the call is closed. */
  thanks_on_close: boolean
  closed_template: string | null
  /** The starting texts and the blanks the server fills (GET only). */
  member_defaults?: { template: string; thanks: string; closed: string; blanks: string[] }
  /** Only the channels the operator has wired up; a channel with no row has
   *  nothing to override. */
  channels: ChannelWrapper[]
  unanswered_after_seconds: number | null
  stalled_after_seconds: number | null
  relocation_ack_seconds: number | null
  unanswered_rebroadcasts: boolean
  /** How long after closing a call can be reopened (072); 0 = never. */
  reopen_window_minutes: number
  /** Sign a screen out after this long with nobody using it (075). */
  signout_idle_minutes: number
  /** The combined rebroadcast (073). */
  combined_rebroadcast: CombinedSettings
  place_match_radius_metres: number | null
  equipment_default_reminder_days: number | null
  equipment_requires_depot_check: boolean
  departments_enabled: boolean
  incident_retention_days: number | null
  recording_retention_days: number | null
  copy_fleet_recordings: boolean
}

/** The solid marks a dispatcher can stamp on a call (059), as the server
 *  spells them. The screen draws each in a shade that reads on both themes. */
export type MarkColour = 'red' | 'purple' | 'blue' | 'green' | 'orange' | 'black'
export const MARK_COLOURS: MarkColour[] = ['red', 'purple', 'blue', 'green', 'orange', 'black']

export type Incident = {
  id: string
  number: string
  nature_label: string
  /** A solid mark a dispatcher stamped on this call to follow it, seen by
   *  every dispatcher; null is none. The light tint every open call is drawn
   *  in is chosen on the screen from the number and is not stored. */
  mark_colour: MarkColour | null
  priority: number
  status: 'open' | 'accepted' | 'on_scene' | 'covered' | 'closed'
  called_at: string
  broadcast_at: string | null
  accepted_at: string | null
  on_scene_at: string | null
  covered_at: string | null
  closed_at: string | null
  /** Logged afterwards with no came-in / closed time (083): the column only
   *  puts the call on its day -- shown "not known", never timed. */
  called_at_unknown?: boolean
  closed_at_unknown?: boolean
  /** The day it came in on the organization's clock, when its time is not
   *  known (083); null otherwise. */
  called_day?: string | null
  caller_name: string | null
  /** The caller asked not to be identified (061): name, number and phone-book
   *  link were deliberately not kept. Says why they are empty. */
  anonymous: boolean
  /** How urgently the CALLER needs help (062), 1 Critical .. 5 Routine,
   *  independent of `priority`; null is "not said". */
  caller_urgency: number | null
  /** How many people rang about it (063): the caller plus every report. */
  caller_count: number
  /** Logged afterwards from pen and paper (078): nothing was sent. */
  logged_afterwards?: boolean
  caller_on_scene: boolean | null
  relationship_label: string | null
  relocated_count: number
  has_open_equipment: boolean
  resolution_label: string | null
  /** What the dispatcher says over the radio -- FFT for a front flat tire.
   *
   *  AS THE SERVER STORED IT, NEVER COMPOSED HERE. It is built from the
   *  nature's code and each answer's piece as they were when the call was
   *  taken, so a code renamed since changes nothing an old call says -- and an
   *  answer kept under a question since retired still carries its piece,
   *  which today's tree no longer knows. Null when the nature has no code. */
  call_code: string | null
  location: string | null
  responder_count: number
  /** Why this call needs attention, from the organization's own windows.
   *
   *  not_broadcast — taken and never sent. Nobody has been asked.
   *  relocation    — it moved and the responder has not confirmed he saw.
   *                  He is driving to the old address.
   *  unanswered    — sent, nobody has taken it.
   *  stalled       — taken, then silence.
   *
   *  Computed by the server so the queue, the count above it, and anything
   *  added later cannot disagree. */
  escalation: 'none' | 'not_broadcast' | 'relocation' | 'unanswered' | 'stalled'
  /** How long it has been in that state, from the event the window applies
   *  to — so "unanswered 7m" means seven minutes since it went out. */
  escalation_seconds: number | null
  /** A safety answer was given — "is a child locked inside?" answered yes.
   *
   *  READ FROM THE ANSWER, NEVER INFERRED FROM PRIORITY 1. The raise is what
   *  usually carries it, but the dispatcher is allowed to lower a priority the
   *  answer raised, and a call opened with an urgency he chose is never raised
   *  at all. In both of those the answer is still the reason to look. */
  has_alert: boolean
}

export type ThreadEvent = {
  occurred_at: string
  /** Which clock this came from. The PBX and WalkieFleet do not share one,
   *  so without this the thread goes subtly out of order. */
  source_system: 'crm' | 'vitalpbx' | 'walkiefleet' | 'radio_app'
  kind: string
  actor_type: string | null
  actor_label: string | null
  summary: string
  detail: Record<string, unknown> | null
  corrected_from: string | null
}

export type Responder = {
  id: string
  member_id: string
  who: string
  role: 'primary' | 'backup'
  /** Null when nobody knows when he took it (083): "time not recorded". */
  accepted_at: string | null
  accepted_via: 'radio' | 'card'
  on_scene_at: string | null
  released_at: string | null
  declared_eta_bucket: string | null
  measured_eta_seconds: number | null
  relocation_ack: 'n/a' | 'pending' | 'confirmed' | 'released'
  /** A text or an email can reach him (082), for the note ticks. */
  reachable?: boolean
  /** Taken off the call, logged by mistake (080): kept, crossed out, and
   *  counted nowhere. `released_at` is set with it. */
  removed_at?: string | null
  removed_by_label?: string | null
  removed_reason?: string | null
}

export type IncidentLocation = {
  sequence: number
  label: string
  moved_at: string
  reported_by: string | null
  place_id: string | null
  /** As given for this call, frozen (054). */
  cross_street_a: string | null
  cross_street_b: string | null
  /** What the caller said about finding this place, for this call (060). */
  note: string | null
  /** Where, in words that are not an address (071). */
  details?: string | null
}

/** Another caller about the same call (063): who, what he added, and his
 *  answers where they differed. */
export type IncidentReport = {
  id: string
  sequence: number
  caller_name: string | null
  caller_phone: string | null
  relationship_label: string | null
  said: string | null
  answers: Record<string, string> | null
  /** He asked not to be identified: kept, marked, never given out (061). */
  anonymous: boolean
  reported_at: string
  recorded_by_label: string | null
}

/** Another number to reach the caller on, given for THIS call (055). */
export type CallbackNumber = {
  id: string
  /** "Home", "Wife" -- free text, and optional. */
  label: string | null
  /** As typed: this is what somebody dials. */
  phone_raw: string
  /** `app/phones.py`'s reading of it; null when it could not be read. */
  phone_e164: string | null
  added_at: string
  added_by_label: string | null
  /** WHERE IT IS IN THE PHONE BOOK NOW (25 Oct 2026), read each time: the
   *  record holding it -- the caller's own first -- or null when nobody's. */
  book_contact_id?: string | null
  book_name?: string | null
  book_is_callers?: boolean
  /** On the row just added, why it is not in the phone book. */
  book_note?: string | null
}

/** Where another number on a call goes in the phone book (25 Oct 2026). */
export type BookSave = 'caller' | 'someone' | 'no'

/** A call closed a moment ago, still reopenable (072). */
export type ClosedCall = {
  id: string
  number: string
  nature_label: string
  call_code: string | null
  closed_at: string
  resolution_label: string | null
  closed_by_label: string | null
  location: string | null
  anonymous: boolean
  /** Until when it can be reopened; after that it is Past calls' only. */
  reopen_until: string
}

/** One of a call's natures (076), in order; position 0 is the first. */
export type CallNature = {
  position: number
  nature_id: string | null
  nature_label: string
  nature_code?: string | null
}

/** One time corrected on a closed call (078): what it was, what it became,
 *  who and why. `responder_id` null is one of the call's own times; `field`
 *  'added' is a responder added afterwards. */
export type IncidentCorrection = {
  responder_id: string | null
  /** A time (`called_at` ... `closed_at`, `added`), or one of the call's
   *  words (079): `caller_name`, `nature`, `answer`, `address` ... */
  field: string
  was: string | null
  became: string | null
  /** For words (079): which question or other number, and what it said. */
  subject?: string | null
  was_text?: string | null
  became_text?: string | null
  reason: string
  corrected_by_label: string
  corrected_at: string
  /** Which note, for a note corrected (081). */
  note_id?: number | null
}

/** A note on a call (081): internal, never sent; or for the responder, sent
 *  to the member going. Added, never written over. */
export type CallNote = {
  id: number
  kind: 'internal' | 'responder'
  body: string
  /** Typed on Take a call, rather than added on the open call. */
  at_intake: boolean
  written_by_label: string
  written_at: string
  /** For the responder's: who a details message after it reached, and who
   *  is going and has not had it yet. */
  sent_to?: { who: string; at: string }[]
  /** On its way to them: a details message with this note still queued. */
  sending?: string[]
  not_yet?: string[]
  /** The same members as `not_yet`, by id, for "send it to him" (082). */
  not_yet_ids?: string[]
  /** Going, and no text or email reaches him: told on the radio. */
  radio_only?: string[]
}

/** What sending a note to the members going did (082). */
export type NoteSent = {
  sent: boolean
  to?: string[]
  by?: string[]
  radio_only?: string[]
  detail?: string
}

export type IncidentDetail = Incident & {
  /** The number he called from, as typed at intake. */
  caller_phone: string | null
  /** Every value corrected, oldest first (078, 079). */
  corrections?: IncidentCorrection[]
  /** What a correction form is opened on (079): sent back, so a call that
   *  moved since is refused rather than written over. */
  fingerprint?: string | null
  /** The call's notes, oldest first (081). */
  call_notes?: CallNote[]
  /** What came in from the phone system (079): kept as it came, never
   *  corrected. */
  caller_phone_from_phone?: boolean
  caller_name_from_phone?: boolean
  relationship_id?: string | null
  /** The first nature's id; null when it was typed rather than chosen. */
  nature_id?: string | null
  /** Its natures in order (076); one for nearly every call. */
  natures?: CallNature[]
  /** The other numbers to reach him on, in the order given. */
  callback_numbers: CallbackNumber[]
  /** The address as a record, with whatever is known about the building. */
  place: Place | null
  /** Where the dispatcher put the pin on this call, if he moved it. Null on
   *  every call nobody corrected, which is most of them. */
  pinned: PinnedSpot | null
  outcome_note: string | null
  source: string
  logged_after_outage: boolean
  /** The area the broadcast named instead of the street, when one was pressed.
   *  How the location was WRITTEN, never who was sent it. */
  broadcast_area: string | null
  thread: ThreadEvent[]
  responders: Responder[]
  locations: IncidentLocation[]
  /** Every caller after the first, in the order they rang (063). */
  reports: IncidentReport[]
  /** What was answered, frozen. */
  answers: IncidentAnswer[]
  /** What this nature asks, in full — the same `tree_for` payload the intake
   *  screen gets, carried WITH the call so the working form is not a second
   *  request that can be stale. Empty when the nature was typed rather than
   *  chosen: there is no list to ask questions from, and the answers already
   *  recorded still read correctly, which is what freezing them is for. */
  questions: CallQuestion[]
  /** How many VISIBLE questions nobody has answered yet, decided by the server
   *  from the same `visible()` the list under it comes from. One piece of
   *  arithmetic, not two — the escalation queue cost a coordinator who set five
   *  minutes the hard-coded seven for exactly this. NOTHING refuses anything on
   *  the strength of it: not one question is mandatory. */
  answers_outstanding: number
}

export type RadioSeat = {
  id: string
  label: string
  provisioned: boolean
  fleet_account_ready: boolean
  /** Provisioned AND with a fleet account behind it. Anything less and the
   *  panel would load onto nothing. */
  usable: boolean
  free: boolean
  held_by: string | null
  held_by_me_here: boolean
  /** I hold it, in another tab. Refused rather than allowed: two tabs in one
   *  browser share a device id, and two fleet sessions on one device id
   *  silently empties recordings. */
  held_by_me_elsewhere: boolean
}

export type RadioClaim = {
  available: boolean
  claimed?: boolean
  connections: RadioSeat[]
  connection?: RadioSeat | null
  token?: { token: string; base_url: string; expires_in: number } | null
  reason?: string
}

/** One id per TAB, not per browser and not per person.
 *
 *  sessionStorage is per tab and survives a reload; localStorage is shared
 *  across tabs, which is exactly the case that must not be allowed to hold two
 *  connections. If storage is unavailable the id lives for the lifetime of
 *  this module, which is still per tab. */
let cachedScreenId: string | null = null
export function screenId(): string {
  if (cachedScreenId) return cachedScreenId
  const KEY = 'advcrm_screen_id'
  try {
    const existing = sessionStorage.getItem(KEY)
    if (existing) {
      cachedScreenId = existing
      return existing
    }
  } catch { /* private mode, or storage blocked */ }
  const fresh = crypto.randomUUID()
  try { sessionStorage.setItem(KEY, fresh) } catch { /* ignore */ }
  cachedScreenId = fresh
  return fresh
}

export type ContactPhone = {
  id: string
  /** Digits only. What caller ID is matched against. */
  phone_e164: string
  /** As it was typed, for reading. */
  phone_raw: string | null
  /** 'mobile', 'home', 'office' — free text, because somebody will want
   *  'the shop' and a fixed list would make that impossible. */
  label: string | null
  is_primary: boolean
  source: 'manual' | 'import' | 'call'
}

export type SiteNote = {
  id: string
  body: string
  severity: 'info' | 'caution' | 'danger'
  /** Never optional on screen. Add-only notes eventually contradict each other
   *  — the dog dies, the gate code changes — and the plan is explicit that the
   *  date and the author are the mitigation rather than the cure. A responder
   *  has to be able to see a note is three years old and weigh it. */
  author_label?: string
  written_at?: string
  /** Did the signed-in dispatcher write this one?
   *
   *  COMPUTED ON THE SERVER, from `site_notes.author_user_id`, which never
   *  leaves it. Superseding or retiring your OWN note is always allowed;
   *  somebody else's is `calls.incident_record.edit_others_note`, which the
   *  seeded Dispatcher role deliberately does not hold. Without this the
   *  screen could not tell the two apart, so it drew Replace and Retire on
   *  every note and let a dispatcher retype a gate code into a box that could
   *  never save. */
  mine?: boolean
}

export type Place = {
  id: string
  label: string
  house_number: string | null
  street: string | null
  unit: string | null
  building: string | null
  city: string | null
  region: string | null
  postal_code: string | null
  country: string | null
  /** True of the building rather than of one call to it. */
  entry_codes: string | null
  /** The two roads either side, remembered for the next call here (054). */
  cross_street_a: string | null
  cross_street_b: string | null
  kind: 'validated' | 'pinned' | 'raw'
  latitude: number | null
  longitude: number | null
  /** The address as the map service understands it. Worth showing beside the
   *  address as typed: when the two disagree, the dispatcher is looking at the
   *  wrong building and can see it. */
  formatted_address: string | null
  /** When the map was last ASKED, whether or not it answered. Distinguishes an
   *  address nobody has looked up from one looked up and not found — they have
   *  the same empty coordinates and need opposite handling. */
  geocoded_at: string | null
  /** Why there is no pin, in words. A blank space under a map reads as "still
   *  loading" and a dispatcher waits for it. */
  geocode_error: string | null
  notes: SiteNote[]
}

export type InboundMessage = {
  id: string
  body: string | null
  received_at: string
  read_at: string | null
  media_urls: string[] | null
  incident_id: string | null
  incident_number: string | null
  nature_label: string | null
  /** Who it was from. The NUMBER is deliberately not here: a screen a
   *  dispatcher photographs is a log, and CLAUDE.md forbids a number in one.
   *  An unrecognised sender is reported as unknown, and the phone book is
   *  where a number to ring back actually lives. */
  member_name: string | null
  callsign: string | null
  contact_name: string | null
  unknown_sender: boolean
}

/** THE MESSAGING TAB (items 20-24, 23 Sep 2026). One conversation per person:
 *  a member (all his numbers together), somebody in the phone book, or an
 *  unknown number. The key names the person, never the number. */
export type ConversationFilter = 'unread' | 'all' | 'members' | 'phonebook' | 'unknown' | 'on_call'
export type ConversationKind = 'member' | 'contact' | 'unknown'
export type Conversation = {
  key: string
  kind: ConversationKind
  name: string | null
  callsign: string | null
  on_duty: boolean | null
  /** Digits, and ONLY for an unknown sender (the customer, Q2 A): a member's
   *  or a contact's number stays behind the name. */
  number: string | null
  unread: number
  last_at: string
  last_body: string | null
  last_direction: 'in' | 'out'
  last_media: number
  incident_id: string | null
  incident_number: string | null
  /** For a role that sees only the texts of live calls (075): the call this
   *  conversation belongs to, and when it clears from the tab. */
  live_call?: { id: string; number: string; open: boolean; clears_at: string | null } | null
}
export type ThreadMedia = {
  id: string
  mime: string | null
  size: number | null
  /** Fetched and kept. Otherwise `error` says why not, or it is on its way. */
  ready: boolean
  error: string | null
}
export type ThreadMessage = {
  id: string
  /** 'group': a group message he was sent (066), shown so a reply reads
   *  under the question it answers. */
  direction: 'in' | 'out' | 'group'
  at: string
  body: string | null
  // in
  read_at?: string | null
  incident_id?: string | null
  incident_number?: string | null
  incident_open?: boolean | null
  media?: ThreadMedia[]
  /** Attachments the text carried before 064 kept them: the link has expired. */
  media_pending?: number
  // out
  status?: 'queued' | 'sent' | 'delivered' | 'failed'
  provider_status?: string | null
  error?: string | null
  sent_by_label?: string | null
  delivered_at?: string | null
  /** Pictures we sent with it (068), shown from our own copy. */
  sent_media?: { id: string; mime: string; size: number }[]
  // group
  subject?: string | null
  audience?: string[]
  channels?: string[]
}
export type ConversationThread = {
  key: string
  kind: ConversationKind
  name: string | null
  callsign: string | null
  on_duty: boolean | null
  member_id: string | null
  contact_id: string | null
  number: string | null
  can_reply: boolean
  sms_on: boolean
  messages: ThreadMessage[]
}
export type OutboundReply = {
  id: string
  status: 'queued' | 'sent' | 'delivered' | 'failed'
  error: string | null
  provider_ref: string | null
  body: string
}

export type Vehicle = {
  id: number
  name: string
  category: string | null
  /** Traccar's own word: online, offline, unknown. */
  status: string
  disabled: boolean
  last_update: string | null
  latitude: number | null
  longitude: number | null
  /** Traccar reports knots. Nobody dispatching a van thinks in knots, so the
   *  screen converts. */
  speed_knots: number | null
  course: number | null
  position_at: string | null
  /** Who is driving, from Traccar's driver records — normally an iButton or
   *  RFID fob. Null means nobody has identified themselves, NOT that the
   *  vehicle is empty. */
  driver: string | null
  /** No fix. Listed but never pinned: a coordinate is never guessed, and a
   *  stale pin is a claim about where something is now. */
  unlocated: boolean
}

export type Fleet = {
  /** False when the GPS platform is unconfigured, switched off, or not
   *  answering. `detail` says which. */
  available: boolean
  detail: string | null
  vehicles: Vehicle[]
}

/** One tickable permission. `note` is the sentence under it, when the leaf
 *  needs one — "Broadcast" reaching real people, for instance. */
export type PermissionNode = { key: string; label: string; note?: string }
export type PermissionGroup = { key: string; label: string; nodes: PermissionNode[] }
export type PermissionBranch = {
  key: string
  label: string
  blurb: string
  groups: PermissionGroup[]
}

export type Role = {
  id: string
  name: string
  description: string | null
  permissions: string[]
  /** The dispatch panel's CEILING, not one of our nodes. 1 is the most
   *  powerful and carries covert listen-in. */
  dispatch_level: number
  /** Administrator and Dispatcher, seeded at provisioning. They can be renamed
   *  and re-ticked but not removed. */
  is_system: boolean
  holders: number
}

export type RoleHolder = {
  id: string
  display_name: string
  login: string
  disabled: boolean
  role_id: string | null
}

export type SearchResults = {
  query: string
  groups: {
    callers?: { id: string; display_name: string | null
                phone_e164: string | null; phone_label: string | null }[]
    places?: { id: string; label: string; kind: string
               located: boolean; notes: number }[]
    incidents?: { id: string; number: string; nature_label: string
                  status: string; called_at: string
                  caller_name: string | null; place_label: string | null }[]
    members?: { id: string; display_name: string; callsign: string | null
                on_duty: boolean; active: boolean }[]
    calls?: { id: string; started_at: string; disposition: string | null
              duration_seconds: number | null; incident_id: string | null
              caller_name: string | null }[]
    /** §09 lists equipment and the server has always returned it. The screen
     *  used to drop it on the floor, so an asset tag printed "Nothing
     *  matched" -- for every role, the wildcard Administrator included. */
    equipment?: { id: string; asset_tag: string; state: string
                  type_name: string; location_name: string | null
                  place_label: string | null; borrower_name: string | null }[]
  }
  empty?: boolean
  too_short?: boolean
  /** The role holds none of the search nodes, so nothing was queried. */
  no_permissions?: boolean
}

export type StockItem = {
  id: string; asset_tag: string; state: string; notes: string | null
  active: boolean; type_id: string; type_name: string; rentable: boolean
  default_deposit: string | null
  location_id: string | null; location_name: string | null
  location_kind: string | null
  loan_id: string | null; flow: string | null; released_at: string | null
  remind_from: string | null; borrower_name: string | null
  receipt_number: string | null; place_label: string | null
}

export type ShelfRow = {
  location_id: string; location_name: string; kind: string
  traccar_device_id: string | null
  type_id: string | null; type_name: string | null
  present: number; expected: number
  /** Present on catalogue rows, absent on shelf rows -- both shapes are used
   *  as a location picker, so the screen reads either. */
  id?: string; name?: string; active?: boolean
}

export type Stock = { items: StockItem[]; locations: ShelfRow[] }

/** One open loan at an address — a line on the collection checklist. */
export type CollectionItem = {
  id: string
  released_at: string
  remind_from: string | null
  item_id: string
  asset_tag: string
  type_name: string
  incident_number: string | null
}

/** What actually came back, and what did not. UNTICKED MEANS STILL THERE: the
 *  server returns the second list rather than assuming a clean run, because
 *  the failure it guards against is somebody driving away one item short and
 *  nobody noticing until the next call needs it. */
export type RunResult = {
  collected: string[]
  still_out: { loan_id: string; asset_tag: string; type_name: string }[]
}

export type OpenLoan = {
  id: string; flow: string; released_at: string; remind_from: string | null
  borrower_name: string | null; borrower_phone: string | null
  receipt_number: string | null
  deposit_expected: string | null; deposit_taken: string | null
  deposit_overridden: boolean
  item_id: string; asset_tag: string; state: string; type_name: string
  place_id: string | null; place_label: string | null
  incident_id: string | null; incident_number: string | null
  contact_name: string | null
  /** Decided in the SQL, never recomputed here -- otherwise the count above
   *  the list and the list itself can disagree, which is what went wrong with
   *  the escalation queue. */
  due: boolean
  days_out: number
}

export type OutNow = { loans: OpenLoan[]; due: number }

export type Catalogue = {
  types: { id: string; name: string; default_deposit: string | null
           rentable: boolean; retired_at: string | null }[]
  locations: { id: string; name: string; kind: string
               traccar_device_id: string | null; active: boolean }[]
}

export type ResponseFigures = {
  calls: number
  /** How many of those calls the median is actually made of. Without it the
   *  headline is unverifiable — "median 8 minutes" over three timed calls out
   *  of twelve looks identical to the honest version. */
  timed: number
  accepted: number
  median_minutes: number | null
  mean_minutes: number | null
  median_accept_minutes: number | null
}

export type Dashboard = {
  timezone: string
  today: ResponseFigures
  month: ResponseFigures
  last_month: ResponseFigures
  open: { total: number; unaccepted: number; accepted: number
          on_scene: number; covered: number }
  members: { active_this_week: number; on_roster: number; on_duty: number }
  /** Named so the screen can say why a tile is absent rather than leaving a
   *  gap somebody reads as a bug. */
  not_built: string[]
}

export type ReportRows = {
  start: string
  end: string
  rows: {
    number: string; called_at: string; nature_label: string
    place_label: string | null; priority: number; status: string
    resolution_label: string | null; responders: number
    response_minutes: number | null; total_minutes: number | null
    logged_after_outage: boolean
    /** Logged afterwards from paper, and times corrected since (078). */
    logged_afterwards?: boolean; corrected?: boolean
    /** Logged with a came-in or closed time nobody knew (083). */
    times_not_known?: boolean; called_at_unknown?: boolean
  }[]
  chart: {
    timezone: string
    hours: { hour: number; calls: number }[]
    days: { day: number; calls: number }[]
  }
  /** Calls per nature (076); a call with several natures counts under each. */
  by_nature?: { nature: string; calls: number; with_others: number }[]
}

export type MemberRows = {
  start: string
  end: string
  rows: {
    display_name: string; callsign: string | null; active: boolean
    calls: number; as_primary: number; as_backup: number
    hours: number | null; first_call: string | null; last_call: string | null
  }[]
}

export type MapSettings = {
  /** False when no map is configured, so the screen can say so rather than
   *  drawing an empty grey square that reads as a fault. */
  enabled: boolean
  tile_url: string | null
  /** A condition of using OpenStreetMap's tiles. Travels with them so a change
   *  of provider cannot leave the wrong credit under the map. */
  attribution: string | null
  centre: { latitude: number; longitude: number } | null
  /** WHAT IS DRAWN UNDER THE MARKERS (migration 056). 'google' only for an
   *  organization on map level B with a browser key the server could read;
   *  every other case is 'osm'. OPTIONAL, because a server from before 056
   *  sends none and that must keep meaning OpenStreetMap -- and `tile_url`
   *  stays required on level B too, since it is what a Google map that will
   *  not load falls back to. `lib/basemap.ts` is the one reader. */
  base?: 'osm' | 'google'
  /** The Maps JavaScript API key, present only with `base: 'google'`. Public
   *  by design -- Google restricts it to this site's hostnames -- and still
   *  never logged, and never served to an organization on any other level. */
  google_browser_key?: string | null
  /** A sentence when the level asks for a Google map and the server already
   *  knows it cannot draw one (no key, or a key it could not decrypt). */
  base_note?: string | null
}

export type Contact = {
  id: string
  display_name: string | null
  /** The caller's OWN address, as one readable line. Not the incident's —
   *  those coincide only when the caller relationship says they do. Composed
   *  by the server from the parts below, so the line and the parts cannot
   *  disagree. */
  address: string | null
  /** And in parts, because the apartment is what a responder needs on his
   *  card and the postal code is what a caller reads back accurately. */
  house_number: string | null
  street: string | null
  unit: string | null
  building: string | null
  city: string | null
  region: string | null
  postal_code: string | null
  country: string | null
  /** Gate codes, where the buzzer is. Attached to the address. */
  entry_codes: string | null
  cross_street_a: string | null
  cross_street_b: string | null
  place_id: string | null
  /** Entry codes, a dog, where the gate is. Attached to the address, and they
   *  travel to whoever accepts the call. */
  site_notes: SiteNote[]
  /** Every number this caller rings from. The same man calls from his mobile,
   *  then his office, then his house — with two fixed columns the third either
   *  overwrote the second or started a duplicate person, and then his history
   *  split across both records. */
  phones: ContactPhone[]
  /** The primary, for the places that show exactly one. Derived from `phones`
   *  by the server so the two cannot disagree. */
  phone_e164: string | null
  email: string | null
  language: string | null
  notes: string | null
  source: 'manual' | 'import' | 'call'
  created_at: string
  updated_at: string
  /** Set only by a create that found the number already there. Not an error:
   *  a dispatcher adding a caller mid-call is handed the existing record. */
  already_existed?: boolean
  /** The SECOND number typed on the add form belonged to somebody else, so it
   *  was refused and this names who holds it. The contact itself was created.
   *
   *  Deliberately not fatal on the server -- the caller being added is real and
   *  is saved, and the number can then be attached on purpose from the phone
   *  book. But that only works if the screen SAYS SO: nothing read this field
   *  for the first two weeks of its existence, so the dispatcher got a green
   *  success message and a record with one number fewer than they typed. */
  second_number_conflict?: string | null
}

export type ContactHistory = {
  /** Twenty as a passer-by, five for himself, ten for somebody else. The
   *  breakdown is the point -- a single count says nothing about what kind of
   *  caller this is. */
  by_relationship: Array<{ relationship: string; calls: number }>
  incidents: Array<{
    id: string; number: string; called_at: string; closed_at: string | null
    status: string; nature_label: string; resolution_label: string | null
    relationship_label: string | null; location: string | null
    /** How it ended, in words -- the closing note. */
    outcome_note: string | null
  }>
  calls: Array<{
    started_at: string; ended_at: string | null
    duration_seconds: number | null; incident_id: string | null
    /** NULL means the PBX said nothing either way, and that is NOT `missed`.
     *  See CLAUDE.md: defaulting it would turn every call in the system into a
     *  confident false missed call the moment DialEnd stops arriving. */
    disposition?: string | null
    phone_e164?: string | null
  }>
}

/** (845) 925-4843. One definition, so the phone book, the pop and the call
 *  record do not each invent their own. Anything that is not a plain ten-digit
 *  North American number is shown untouched rather than mangled. */
/** A phone number, for reading.
 *
 *  THE ROSTER IS WORLDWIDE, so this can no longer assume ten digits and a
 *  North American shape. It used to force every value through
 *  `(NNN) NNN-NNNN`, which is right for Montreal and wrong for London, Tel
 *  Aviv and Oslo -- and the stored form is now full E.164, so every number
 *  arrives here carrying its own country code.
 *
 *  Formatting only. It never decides what a number IS -- that is
 *  `app/phones.py` on the server, and there is exactly one of it. A display
 *  helper that started normalising would be the phone-book bug arriving in the
 *  browser, where it would be even harder to see.
 *
 *  Anything it does not recognise is returned untouched rather than mangled
 *  into something that looks wrong. */
export function prettyPhone(value: string | null | undefined): string {
  if (!value) return ''
  const d = value.replace(/\D/g, '')

  // North American, with or without the country code: the form most of this
  // product's users read fastest.
  const na = d.length === 11 && d.startsWith('1') ? d.slice(1) : d
  if (na.length === 10 && (value.startsWith('+1') || !value.startsWith('+'))) {
    return `(${na.slice(0, 3)}) ${na.slice(3, 6)}-${na.slice(6)}`
  }

  // Anything else keeps its `+` and is grouped loosely for readability. Not
  // per-country formatting: doing that properly means shipping libphonenumber
  // to the browser, and a wrong national format is harder to read than an
  // honest international one.
  if (value.startsWith('+') && d.length > 6) {
    return `+${d.slice(0, d.length - 6)} ${d.slice(-6, -3)} ${d.slice(-3)}`
  }
  return value
}

export type CallRecord = {
  id: string
  started_at: string
  answered_at: string | null
  ended_at: string | null
  /** What we concluded. NULL means the PBX told us nothing either way — shown
   *  as "outcome not recorded" rather than guessed at, because a call wrongly
   *  marked missed sends somebody ringing back a caller already spoken to. */
  disposition: 'answered' | 'missed' | 'busy' | 'failed' | 'voicemail' | null
  /** Asterisk's own word, kept so a wrong conclusion is diagnosable. */
  dial_status: string | null
  hangup_cause: string | null
  /** How long it rang. */
  ring_seconds: number | null
  /** Talk time. NULL when nobody answered — deliberately not the same number. */
  duration_seconds: number | null
  /** Who took it (section 12). */
  extension: string | null
  /** Which of the organization's numbers was rung. */
  dialled: string | null
  phone_e164: string | null
  direction: 'in' | 'out'
  contact_id: string | null
  contact_name: string | null
  incident_id: string | null
  incident_number: string | null
  incident_status: string | null
  handled_at?: string | null
}

export type CallDay = {
  day: string | null
  /** Whether the AMI listener was connected when this was read. An empty list
   *  looks identical whether the shift was quiet or the listener has been down
   *  since six o'clock, and being reassured by the second one is the worst
   *  thing this screen could do. */
  listening: boolean
  timezone: string
  totals: {
    calls: number; missed: number; answered: number; unknown: number
    strangers: number; became_calls: number
  }
  calls: CallRecord[]
}

export const api = {
  radioStatus: (screenId: string) =>
    request<{ available: boolean; assigned_id: string | null
              connections: RadioSeat[]; mine: RadioSeat | null }>(
      `/api/radio/status?screen_id=${encodeURIComponent(screenId)}`),
  radioSignIn: (screenId: string) =>
    request<RadioClaim>('/api/radio/sign-in',
      { method: 'POST', body: JSON.stringify({ screen_id: screenId }) }),
  radioTake: (screenId: string, connectionId: string) =>
    request<{ token: string; base_url: string; expires_in: number; connection: string }>(
      '/api/radio/session',
      { method: 'POST',
        body: JSON.stringify({ screen_id: screenId, connection_id: connectionId }) }),
  radioHeartbeat: (screenId: string, connectionId: string) =>
    request<{ held_by_me: boolean }>('/api/radio/heartbeat',
      { method: 'POST',
        body: JSON.stringify({ screen_id: screenId, connection_id: connectionId }) }),
  radioRelease: (screenId: string, connectionId: string) =>
    request<void>('/api/radio/release',
      { method: 'POST',
        body: JSON.stringify({ screen_id: screenId, connection_id: connectionId }) }),
  radioHolders: () =>
    request<Array<{
      connection_label: string; user_label: string
      acquired_at: string; released_at: string | null; release_reason: string | null
    }>>('/api/radio/holders'),
  ringing: () =>
    request<{
      listening: boolean
      /** How many dispatchers have a screen open right now, and whether this
       *  one is the only one. With one on duty a caller's card opens itself;
       *  with two it must not, or it interrupts somebody mid-call and both
       *  assume the other has it. */
      watching: number
      alone: boolean
      ringing: Array<{
        linkedid: string; caller_number: string; caller_name: string | null
        contact_id: string | null; contact_name: string | null
        dialled: string | null; extension: string | null; started_at: string
        answered_at: string | null; ended_at: string | null
        /** False once the caller has hung up. The card lingers a few minutes:
         *  it vanishing the moment they ring off was taking it away at exactly
         *  the moment the dispatcher needed it. */
        live: boolean
        never_rang: boolean
      }>
    }>('/api/pbx/ringing'),
  extensions: () =>
    request<{
      users: Array<{
        id: string; login: string; display_name: string
        pbx_extension: string | null; disabled: boolean; role_name: string | null
      }>
      inbound_numbers: string[]
      configured: boolean
      unclaimed_extensions: Array<{
        extension: string; calls: number; last_seen: string
      }>
    }>('/api/extensions'),
  extensionAssign: (userId: string, extension: string | null) =>
    request<{ id: string; display_name: string; pbx_extension: string | null }>(
      '/api/extensions/assign',
      { method: 'POST', body: JSON.stringify({ user_id: userId, extension }) }),
  inboundNumbers: (numbers: string[]) =>
    request<{ numbers: string[] }>('/api/extensions/numbers',
      { method: 'PUT', body: JSON.stringify({ numbers }) }),
  incidents: (openOnly = true) =>
    request<Incident[]>(`/api/incidents?open_only=${openOnly}`),
  incident: (id: string) => request<IncidentDetail>(`/api/incidents/${id}`),
  /** ONE REBROADCAST FOR SEVERAL OPEN CALLS (073). */
  combinedWindow: () =>
    request<{ settings: CombinedSettings; calls: CombinedCall[]
              /** Open calls left out: never broadcast yet. */
              not_broadcast: number }>('/api/combined-rebroadcast'),
  combinedPreview: (items: { incident_id: string; line: string }[]) =>
    request<{ body: string; header: string | null; footer: string | null
              whole: string; characters: number }>('/api/combined-rebroadcast/preview', {
      method: 'POST', body: JSON.stringify({ items }),
    }),
  combinedSend: (body: { items: { incident_id: string; line: string }[]
                         group_ids: string[]; request_key: string }) =>
    request<{ id: string; repeat: boolean; recipients?: number; calls?: number }>(
      '/api/combined-rebroadcast', { method: 'POST', body: JSON.stringify(body) }),
  /** SEVERAL DRAFTS, ONE FIRST BROADCAST (074): the message before any of
   *  them is a call, each as its own form would preview it. */
  draftsPreview: (items: Record<string, unknown>[], groupIds: string[]) =>
    request<{ body: string; header: string | null; footer: string | null
              whole: string; characters: number; recipients: number
              /** Why the send would be refused, asked before any draft opens. */
              problem: string | null }>('/api/combined-rebroadcast/first/preview', {
      method: 'POST', body: JSON.stringify({ items, group_ids: groupIds }),
    }),
  /** And the send, for the calls just opened from them, in the same order. */
  draftsSend: (body: { incident_ids: string[]; group_ids: string[]; request_key: string }) =>
    request<{ id: string; repeat: boolean; recipients?: number; calls?: number
              /** Calls left out: already broadcast or closed, by name. */
              skipped?: string[] }>(
      '/api/combined-rebroadcast/first', { method: 'POST', body: JSON.stringify(body) }),
  /** Calls closed inside the organization's reopen window (072). */
  recentlyClosed: () => request<ClosedCall[]>('/api/incidents/recently-closed'),
  /** Back on the board as it was before it was closed (072). Nothing is sent. */
  incidentReopen: (id: string) =>
    request<{ status: string }>(`/api/incidents/${id}/reopen`, { method: 'POST' }),
  /** Open a call. `priority` MUST BE ABSENT unless the dispatcher pressed an
   *  urgency chip, and `JSON.stringify` dropping an `undefined` is how this
   *  client says so.
   *
   *  It is not a nicety. A safety answer raises the call to the top of the
   *  queue, and it must not do that over an urgency a human chose — with a
   *  default of 3 there is no way to tell "he pressed Normal" from "he never
   *  looked", so the raise would either always fire or never fire. The server
   *  still defaults to 3 when nobody chose. */
  incidentOpen: (body: Record<string, unknown>) =>
    request<{
      id: string; number: string; answers: AnswerOutcome
      /** The other numbers: how many were kept, and a sentence when not all. */
      numbers?: { stored: number; note: string | null }
      /** The two notes (081): how many were kept, and a sentence when not. */
      notes?: { stored: number; note: string | null }
    }>('/api/incidents', { method: 'POST', body: JSON.stringify(body) }),
  /** A call that already happened (078): the open body plus `called_at`,
   *  `went` and `close`. Saved; NOTHING is sent. */
  incidentLogAfterwards: (body: Record<string, unknown>) =>
    request<{
      id: string; number: string; answers: AnswerOutcome
      numbers?: { stored: number; note: string | null }
      notes?: { stored: number; note: string | null }
      logged?: { status: string; responders: number }
    }>('/api/incidents/log-afterwards', { method: 'POST', body: JSON.stringify(body) }),
  /** Correct a closed call's times and who went (078). `call` and each
   *  responder carry ONLY the times edited; null clears one. */
  incidentCorrect: (id: string, body: {
    reason: string
    call: Record<string, string | null>
    responders: ({ id: string } & Record<string, string | null>)[]
    added: { member_id: string; role: string; accepted_at: string | null; on_scene_at: string | null }[]
    /** Responders taken off, logged by mistake (080). */
    remove?: string[]
    /** Notes corrected (081): what each says now. */
    notes?: { change: { id: number; body: string }[] }
    /** The words of the call, only those edited (079). */
    details?: Record<string, unknown>
    answers?: Record<string, string>
    natures?: { first?: string | null; add: string[]; remove: number[] }
    numbers?: { change: { id: string; label: string | null; phone: string }[]; add: { label: string | null; phone: string }[] }
    fingerprint?: string
    /** Another day for a call whose came-in time is not known (083). */
    called_day?: string
  }) =>
    request<{
      changes: number
      /** Where it is changed, and how many are still going (079): "whoever
       *  is going still has the old address". */
      where_changed?: boolean; going?: number
    }>(`/api/incidents/${id}/corrections`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  /** Another number to reach the caller on, while the call is open. Answers
   *  with the call's whole list. */
  incidentCallbackAdd: (id: string, label: string | null, phone: string,
                        book?: { save: BookSave; name: string | null }) =>
    request<CallbackNumber[]>(`/api/incidents/${id}/callback-numbers`, {
      method: 'POST', body: JSON.stringify({ label, phone, ...(book ?? {}) }),
    }),
  /** A number already on a call, into the phone book -- open or closed; the
   *  call is not changed (25 Oct 2026). */
  incidentCallbackFile: (id: string, numberId: string, save: 'caller' | 'someone', name: string | null) =>
    request<CallbackNumber[]>(`/api/incidents/${id}/callback-numbers/${numberId}/phone-book`, {
      method: 'POST', body: JSON.stringify({ save, name }),
    }),
  incidentCallbackRemove: (id: string, numberId: string) =>
    request<CallbackNumber[]>(`/api/incidents/${id}/callback-numbers/${numberId}`, {
      method: 'DELETE',
    }),
  /** Another nature on an open call, and one taken off again (076). */
  incidentNatureAdd: (id: string, natureId: string) =>
    request<{ natures: CallNature[]; nature_label: string; call_code: string | null }>(
      `/api/incidents/${id}/natures`, {
        method: 'POST', body: JSON.stringify({ nature_id: natureId }),
      }),
  incidentNatureRemove: (id: string, position: number) =>
    request<{ natures: CallNature[]; nature_label: string; call_code: string | null }>(
      `/api/incidents/${id}/natures/${position}`, { method: 'DELETE' }),
  /** Answer the nature's questions on a call that is already open — the half
   *  the intake form deliberately does not block on.
   *
   *  UNGATED, like opening the call itself. It answers with the WHOLE picture
   *  because one answer can reveal three questions and hide four, and a screen
   *  working the new shape out for itself would be a second copy of `visible()`
   *  deciding what is STORED rather than only what is drawn.
   *
   *  A MISSING KEY IS NOT AN EMPTY ONE: keys that are not sent are left exactly
   *  as they stand, and a key sent as "" clears that answer. */
  incidentAnswers: (id: string, answers: Record<string, string>) =>
    request<AnswerOutcome & {
      answers: IncidentAnswer[]
      questions: CallQuestion[]
      priority: number | null
    }>(`/api/incidents/${id}/answers`, {
      method: 'POST', body: JSON.stringify({ answers }),
    }),
  /** `groupIds` null or absent is everyone on duty; a list is the union of
   *  those groups (054). The server refuses a list that reaches nobody. */
  incidentBroadcast: (id: string, kind = 'initial', groupIds?: string[] | null) =>
    request<{ recipients: number; body: string }>(`/api/incidents/${id}/broadcast`, {
      method: 'POST',
      body: JSON.stringify(groupIds && groupIds.length ? { kind, group_ids: groupIds } : { kind }),
    }),
  /** Who a broadcast can be aimed at, and how many on duty each reaches now.
   *  The area names double as the Take a call box's area buttons -- as labels
   *  only, never as who is sent it. */
  broadcastAudiences: () =>
    request<{
      everyone: number
      groups: { id: string; name: string; kind: 'area' | 'category' | 'list'; reach: number }[]
    }>('/api/incidents/audiences'),
  /** The exact message a call would send, before it exists. Writes nothing. */
  incidentPreviewBroadcast: (body: Record<string, unknown>) =>
    request<{
      body: string; header: string | null; footer: string | null
      whole: string; characters: number
    }>('/api/incidents/preview-broadcast', { method: 'POST', body: JSON.stringify(body) }),
  /** To the members going: `site` is gate codes and site notes, `callback` the
   *  caller's number, `details` the full address and answers again. */
  incidentSend: (id: string, what: 'details' | 'site' | 'callback') =>
    request<{
      recipients: number; channels: number; radio_only: string[]
      /** Who it went to, by name, and how: "text", "email". */
      to: string[]; by: string[]
    }>(
      `/api/incidents/${id}/send`, { method: 'POST', body: JSON.stringify({ what }) }),
  /** The cross streets THIS call carries -- what a re-broadcast and the
   *  details say. The address book's copy is `placeUpdate`. */
  incidentCrossStreets: (id: string, a: string | null, b: string | null) =>
    request<{ cross_street_a: string | null; cross_street_b: string | null }>(
      `/api/incidents/${id}/cross-streets`, {
        method: 'POST', body: JSON.stringify({ cross_street_a: a, cross_street_b: b }),
      }),
  /** Open calls this caller may be ringing about (063). */
  incidentSimilar: (body: { location_label: string; nature_label: string; nature_id: string | null }) =>
    request<{
      id: string; number: string; call_code: string | null; nature_label: string
      called_at: string; location: string | null
      responder_count: number; caller_count: number; why: 'address' | 'nature'
    }[]>('/api/incidents/similar', { method: 'POST', body: JSON.stringify(body) }),
  /** Add this caller to an existing call (063). Opens nothing, sends nothing. */
  incidentAddReport: (id: string, body: {
    caller_name: string | null; caller_phone: string | null
    relationship_label: string | null; said: string | null
    answers: Record<string, string> | null
    anonymous: boolean
    /** The draft he was typed into (069), marked joined with the report. */
    draft_id?: string | null
    /** The notes typed while taking him (081), added to that call's. */
    internal_note?: string | null
    responder_note?: string | null
  }) =>
    request<{ id: string; sequence: number; caller_count: number
              /** The notes (081): a sentence when they could not be stored. */
              notes?: { note: string | null } }>(
      `/api/incidents/${id}/reports`, { method: 'POST', body: JSON.stringify(body) }),
  /** CALL DRAFTS (069): the Take-a-call form saved as it is typed, shared by
   *  every dispatcher. A refusal's status says which: 423 somebody else has it
   *  open, 410 it is no longer a draft, 409 it changed since this screen read
   *  it. */
  callDrafts: () => request<CallDraft[]>('/api/call-drafts'),
  callDraftCreate: (data: Record<string, unknown>, id?: string) =>
    request<CallDraft & { data: Record<string, unknown> }>('/api/call-drafts', {
      method: 'POST', body: JSON.stringify({ data, id }),
    }),
  /** One draft with its form, read and not taken: "Broadcast drafts" (074). */
  callDraftOne: (id: string) =>
    request<CallDraft & { data: Record<string, unknown> }>(`/api/call-drafts/${id}`),
  callDraftTake: (id: string, force: boolean) =>
    request<CallDraft & { data: Record<string, unknown> }>(`/api/call-drafts/${id}/take`, {
      method: 'POST', body: JSON.stringify({ force }),
    }),
  /** `keepalive` for the save made as the page goes away: the browser
   *  finishes it after the tab is closed. */
  callDraftSave: (id: string, data: Record<string, unknown>, version: number, keepalive = false) =>
    request<{ version: number; updated_at: string }>(`/api/call-drafts/${id}`, {
      method: 'PUT', body: JSON.stringify({ data, version }), keepalive,
    }),
  callDraftKeep: (id: string, version: number) =>
    request<{ version: number }>(`/api/call-drafts/${id}/keep`, {
      method: 'POST', body: JSON.stringify({ version }),
    }),
  callDraftRelease: (id: string, keepalive = false) =>
    request<void>(`/api/call-drafts/${id}/release`, { method: 'POST', keepalive }),
  callDraftDiscard: (id: string, keepalive = false) =>
    request<void>(`/api/call-drafts/${id}/discard`, { method: 'POST', keepalive }),
  /** How urgently the caller needs help, or null for not said (062). */
  incidentCallerUrgency: (id: string, urgency: number | null) =>
    request<{ caller_urgency: number | null }>(`/api/incidents/${id}/caller-urgency`, {
      method: 'POST', body: JSON.stringify({ urgency }),
    }),
  /** Stamp the call with a solid mark, or null to take it off (059). */
  incidentMark: (id: string, colour: MarkColour | null) =>
    request<{ mark_colour: MarkColour | null }>(`/api/incidents/${id}/mark`, {
      method: 'POST', body: JSON.stringify({ colour }),
    }),
  incidentBroadcasts: (id: string) =>
    request<Array<{
      id: string; kind: string; body: string; sent_at: string
      recipient_count: number
      /** The group names it was aimed at; null is everyone on duty. */
      audience: string[] | null
      /** The carrier accepted it. */
      sent: number
      /** It reached the handset — the text server's delivery receipt. A
       *  stronger claim than `sent`, and still not an acceptance. */
      delivered: number
      failed: number; queued: number
      /** Every status by name, including any this client does not draw yet.
       *  The named fields above were once the whole list, and `delivered`
       *  belonged to none of them. */
      counts: Record<string, number>
      first_error: string | null
    }>>(`/api/incidents/${id}/broadcasts`),
  /** `details` says whether the automatic handover message went out -- the
   *  acceptance is recorded either way. */
  /** The responder picker's search (item 18): a name, a unit or the digits
   *  of a number. Answers with picker rows and ONE WORD saying what matched --
   *  never a number. */
  memberPick: (term: string) =>
    request<{
      id: string; callsign: string | null; display_name: string | null
      label: string; on_duty: boolean; has_radio: boolean
      matched_on: 'unit' | 'name' | 'phone' | null
    }[]>('/api/members/pick', { method: 'POST', body: JSON.stringify({ term }) }),
  incidentAccept: (id: string, body: Record<string, unknown>) =>
    request<{
      who: string
      details?: {
        sent: boolean; channels?: number; radio_only?: string[]; detail?: string
        to?: string[]; by?: string[]
        /** Unticked on the screen: logged as going, nothing sent (078). */
        withheld?: boolean
      }
    }>(`/api/incidents/${id}/responders`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  /** This responder has arrived -- or, with `undo`, that press is taken back.
   *  The call's own on-scene time is the first arrival, kept by the server. */
  incidentResponderOnScene: (id: string, responderId: string, undo = false) =>
    request<{ responder_id: string; on_scene_at: string | null }>(
      `/api/incidents/${id}/responders/${responderId}/on-scene`,
      { method: undo ? 'DELETE' : 'POST' }),
  /** Add to a call's notes (081): internal, or for the responder. */
  incidentCallNote: (id: string, kind: 'internal' | 'responder', body: string,
                     sendTo: string[] = []) =>
    request<{ id: number; sent?: NoteSent }>(`/api/incidents/${id}/call-notes`,
      { method: 'POST', body: JSON.stringify({ kind, body, send_to: sendTo }) }),
  /** "Send it to him" (082): one note for the responder, this line only. */
  incidentCallNoteSend: (id: string, noteId: number, memberIds: string[]) =>
    request<NoteSent>(`/api/incidents/${id}/call-notes/${noteId}/send`,
      { method: 'POST', body: JSON.stringify({ member_ids: memberIds }) }),
  /** Take off a member logged by mistake (080). Marked, never deleted. */
  incidentResponderRemove: (id: string, responderId: string, reason: string | null) =>
    request<{ who: string; already: boolean; promoted?: string | null
              told: null | { told: boolean; why?: string; to?: string; by?: string[] } }>(
      `/api/incidents/${id}/responders/${responderId}/remove`,
      { method: 'POST', body: JSON.stringify({ reason }) }),
  incidentTimeline: (id: string, step: 'on_scene' | 'covered') =>
    request<unknown>(`/api/incidents/${id}/timeline`, {
      method: 'POST', body: JSON.stringify({ step }),
    }),
  /** ADDRESSES THIS ORGANIZATION HAS ALREADY BEEN TO, as the dispatcher types.
   *  A POST for a read, like every search here: an address is a location, and
   *  a location in a query string is written to two access logs. */
  placeSuggest: (term: string, session?: string) =>
    request<{
      suggestions: {
        /** Set for an address this organization has been to; null for one the
         *  map offered, which has no record here yet. */
        place_id: string | null
        label: string
        /** The book's own coordinates when it has them. A map suggestion
         *  depends on the provider: Photon's carries them whenever the result
         *  is precise enough to pin (and never half a pair); Google's carries
         *  none until it is PICKED — resolving one costs a request, and only
         *  the chosen one is worth it. */
        latitude: number | null; longitude: number | null
        /** Remembered on an address he has been to; null from the map. */
        cross_street_a: string | null; cross_street_b: string | null
        provider_place_id: string | null
        source: 'book' | 'map'
      }[]
      /** Said only when a map that should have answered did not, so the
       *  dispatcher knows the list he is looking at is his own addresses. */
      note: string | null
    }>('/api/places/suggest', {
      method: 'POST', body: JSON.stringify({ term, session }),
    }),

  /** Where a map suggestion is, inside the same billing session that found
   *  it. Called for the one suggestion he picks and never for the list. */
  placeResolveSuggestion: (providerPlaceId: string, session?: string) =>
    request<{
      found: boolean
      latitude: number | null; longitude: number | null
      formatted_address: string | null
      detail: string | null
    }>('/api/places/suggest/resolve', {
      method: 'POST',
      body: JSON.stringify({ provider_place_id: providerPlaceId, session }),
    }),

  /** Where is this line of text? Stores nothing, and answers 200 even when the
   *  map does not know it — "ask for a cross street" is an outcome, not a
   *  fault. Called once when an address is settled, NEVER per keystroke. */
  placeLocatePreview: (label: string) =>
    request<{
      found: boolean
      latitude: number | null; longitude: number | null
      formatted_address: string | null
      detail: string | null
    }>('/api/places/locate-preview', {
      method: 'POST', body: JSON.stringify({ label }),
    }),

  /** The call moved: a NEW location on the trail, the old ones kept. The
   *  pin is the picked suggestion's spot when there is one (22 Sep 2026). */
  incidentRelocate: (id: string, body: {
    label: string; place_id?: string | null
    reported_by: 'caller' | 'responder' | 'dispatcher'
    latitude?: number | null; longitude?: number | null
    note?: string | null
  }) =>
    request<{
      sequence: number; responders_asked: number; asked: string[]
      /** The new address, marked MOVED, sent to those going -- or why not. */
      details?: { sent: boolean; to?: string[]; by?: string[]; radio_only?: string[]; detail?: string }
    }>(
      `/api/incidents/${id}/locations`, {
        method: 'POST', body: JSON.stringify(body),
      }),
  /** After a move: he is still going, or he is not. The third state, not
   *  answering, is what the dispatcher chases. */
  incidentRelocationAck: (id: string, responderId: string, stillGoing: boolean) =>
    request<unknown>(`/api/incidents/${id}/responders/${responderId}/relocation-ack`, {
      method: 'POST', body: JSON.stringify({ still_going: stillGoing }),
    }),
  /** The note on the call's current location, corrected (060). */
  incidentLocationNote: (id: string, note: string | null) =>
    request<{ note: string | null }>(`/api/incidents/${id}/location-note`, {
      method: 'POST', body: JSON.stringify({ note }),
    }),
  incidentNote: (id: string, text: string) =>
    request<unknown>(`/api/incidents/${id}/notes`, {
      method: 'POST', body: JSON.stringify({ text }),
    }),
  incidentClose: (id: string, body: Record<string, unknown>) =>
    request<unknown>(`/api/incidents/${id}/close`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  orgSettings: () => request<OrgSettings>('/api/org-settings'),
  /** The member's message on three invented calls, by the server's own
   *  renderer (084), and the blanks nothing fills. */
  memberMessagePreview: (body: MessagesPreviewIn) =>
    request<MessagesPreview>('/api/org-settings/member-message/preview', { method: 'POST', body: JSON.stringify(body) }),
  orgSettingsUpdate: (body: Record<string, unknown>) =>
    request<OrgSettings>('/api/org-settings', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  members: (includeInactive = false) =>
    request<Member[]>(`/api/members?include_inactive=${includeInactive}`),
  memberCreate: (body: Record<string, unknown>) =>
    request<Member>('/api/members', { method: 'POST', body: JSON.stringify(body) }),
  users: () => request<OrgUser[]>('/api/users'),
  userCreate: (body: Record<string, unknown>) =>
    request<OrgUser>('/api/users', { method: 'POST', body: JSON.stringify(body) }),
  userUpdate: (id: string, body: Record<string, unknown>) =>
    request<OrgUser>(`/api/users/${id}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),

  memberUpdate: (id: string, body: Record<string, unknown>) =>
    request<Member>(`/api/members/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  /** THE WHOLE MEMBER COMES BACK FROM ALL THREE OF THESE, never the number
   *  alone. Adding, removing or re-primarying a number changes the member's
   *  own `phone` and `notify_sms`, so a screen handed anything less would have
   *  to work the new values out for itself — a second definition of "his
   *  number" living in the browser. `remove_phone` on the phone book learned
   *  this the same way. */
  memberPhoneAdd: (id: string, body: {
    phone: string; label?: string | null; notify?: boolean | null
    is_primary?: boolean
  }) =>
    request<Member>(`/api/members/${id}/phones`,
      { method: 'POST', body: JSON.stringify(body) }),
  memberPhoneUpdate: (id: string, phoneId: string, body: {
    phone?: string; label?: string | null; notify?: boolean; is_primary?: boolean
  }) =>
    request<Member>(`/api/members/${id}/phones/${phoneId}`,
      { method: 'PATCH', body: JSON.stringify(body) }),
  /** ONE MEMBER, WITH HIS MAILING ADDRESS. `members()` deliberately does not
   *  carry the eight address columns: that list is UNGATED, because the
   *  dispatcher's responder picker needs it and `Dispatch.tsx` fetches it on
   *  every load — so putting a home address on it would hand every volunteer's
   *  address to every dispatcher's browser on every shift, invisibly, since
   *  nothing on that screen draws it.
   *
   *  So the record dialog asks for the full row separately, and this route
   *  carries `settings.members` like the writes beside it. A role that gets the
   *  record read-only never calls this and simply has no address section. */
  memberOne: (id: string) => request<MemberRecord>(`/api/members/${id}`),

  memberPhoneRemove: (id: string, phoneId: string) =>
    request<Member>(`/api/members/${id}/phones/${phoneId}`, { method: 'DELETE' }),

  /** The scans on one member. Metadata only — never a byte of content.
   *
   *  ALL FOUR OF THESE NEED `settings.member_documents`, which is a separate
   *  leaf from `settings.members` and sits on no seeded role: correcting a typo
   *  in a callsign and opening a photograph of somebody's passport are not the
   *  same privilege. */
  memberDocuments: (memberId: string) =>
    request<MemberDocument[]>(`/api/members/${memberId}/documents`),
  /** FormData, so `request` leaves Content-Type alone and the browser writes
   *  the multipart boundary only it knows — see the note in `request`.
   *
   *  A REGISTRATION CALLS THIS TOO, AFTER THE CREATE. `member_documents.member_id`
   *  is NOT NULL, so there is nothing to file a scan against until the member
   *  exists — the registration form holds the chosen `File` in the browser and
   *  calls this the moment the POST answers, rather than staging encrypted
   *  contents on the server attached to nobody. There is no second upload route
   *  and there must not be one. */
  memberDocumentUpload: (
    memberId: string, file: File,
    kind: 'identification' | 'other', note?: string | null,
  ) => {
    const form = new FormData()
    form.append('file', file)
    form.append('kind', kind)
    if (note) form.append('note', note)
    return request<MemberDocument>(`/api/members/${memberId}/documents`, {
      method: 'POST', body: form,
    })
  },
  /** A DOCUMENT id, not a member id, and that is the server's design rather
   *  than a shortcut: a member in the URL could disagree with the document's
   *  real owner, and the only place to catch that is after the bytes have been
   *  decrypted and the audit row committed — which would record a view of a
   *  document nobody was handed.
   *
   *  A FETCH AND A BLOB, NOT AN `<a href>`. The route is a plain authenticated
   *  GET so a link would work, but its errors are JSON — a 404 for a document
   *  that failed its integrity check, a 503 for a SECRET_KEY that has changed —
   *  and a browser NAVIGATES to those, throwing away an open record to show a
   *  page nobody can read. This keeps the refusal inside the dialog, where it
   *  can be a sentence. */
  memberDocumentOpen: async (documentId: string): Promise<Blob> => {
    let res: Response
    try {
      res = await fetch(`/api/member-documents/${documentId}`, {
        credentials: 'same-origin',
      })
    } catch {
      throw new ApiError(0, 'Cannot reach the server.')
    }
    if (!res.ok) {
      const body = await res.json().catch(() => null)
      throw new ApiError(
        res.status,
        body?.detail ?? `Could not open that document (${res.status}).`)
    }
    return res.blob()
  },
  /** Answers with the row that was removed, in the same shape as the listing.
   *
   *  CARRIES THE REASON, because `audit_log.reason` is the one field in this
   *  whole trail that cannot be reconstructed afterwards. The rest — what was
   *  held, who removed it, when — is all recoverable from the row. "Why was
   *  this deleted" is the question that actually gets asked about a document
   *  kept for legal reasons, and the answer exists only in the head of the
   *  person pressing the button, at the moment they press it.
   *
   *  Null when they gave none: an empty string would record a reason that was
   *  never offered. */
  memberDocumentDelete: (documentId: string, reason: string | null = null) =>
    request<MemberDocument>(`/api/member-documents/${documentId}`,
      { method: 'DELETE', body: JSON.stringify({ reason }) }),

  /** The organization's list of qualifications. UNGATED to read, like the
   *  nature-of-call list and the member groups; every write needs
   *  `settings.members`, which is NOT a new leaf — whoever says which
   *  qualifications the organization recognises is whoever says who is on the
   *  roster, and `PLAN.html` section 19 forbids a tick for a distinction the
   *  product draws nowhere. */
  certifications: (includeInactive = false) =>
    request<Certification[]>(
      `/api/member-certifications?include_inactive=${includeInactive}`),
  certificationCreate: (body: { name: string; sort_order?: number }) =>
    request<Certification>('/api/member-certifications',
      { method: 'POST', body: JSON.stringify(body) }),
  /** Rename it, reorder it, or RETIRE it (`active: false`). There is no delete
   *  route and there must not be one. */
  certificationUpdate: (id: string, body: {
    name?: string; sort_order?: number; active?: boolean
  }) =>
    request<Certification>(`/api/member-certifications/${id}`,
      { method: 'PATCH', body: JSON.stringify(body) }),

  /** ALL THREE ANSWER WITH THE MEMBER'S WHOLE LIST, never with the one row that
   *  changed and never 204. The order is a decision made once in SQL — lapsed
   *  first, because that is the row that changes who gets sent — and handing
   *  back a single row would leave the browser working out where it goes, which
   *  is that ordering rewritten in a second place. The member's numbers learned
   *  this the same way.
   *
   *  A RENEWAL IS A PATCH, NOT A SECOND ADD. There is one row per member per
   *  qualification, so a new card is a new expiry date on the row he already
   *  has — two rows would be the state where "is his EMT current?" has two
   *  answers, one expired and one not. `certification_id` is not patchable:
   *  the dates on a holding were copied off a particular card. */
  memberCertificationAdd: (memberId: string, body: {
    certification_id: string
    obtained_on?: string | null
    expires_on?: string | null
    note?: string | null
  }) =>
    request<MemberCertificationRecord[]>(
      `/api/members/${memberId}/certifications`,
      { method: 'POST', body: JSON.stringify(body) }),
  memberCertificationUpdate: (
    memberId: string, holdingId: string,
    body: { obtained_on?: string | null; expires_on?: string | null; note?: string | null },
  ) =>
    request<MemberCertificationRecord[]>(
      `/api/members/${memberId}/certifications/${holdingId}`,
      { method: 'PATCH', body: JSON.stringify(body) }),
  /** The one thing here that is genuinely deleted, and deliberately: nothing
   *  references a holding. It is a statement about what this person can do NOW,
   *  and a statement that is wrong should stop being made rather than be kept
   *  with a flag beside it. */
  memberCertificationRemove: (memberId: string, holdingId: string) =>
    request<MemberCertificationRecord[]>(
      `/api/members/${memberId}/certifications/${holdingId}`,
      { method: 'DELETE' }),

  /** UNGATED to read, like the nature-of-call list: choosing who a broadcast
   *  goes to is part of taking a call. The writes need `settings.members`. */
  memberGroups: (includeInactive = false) =>
    request<MemberGroup[]>(`/api/member-groups?include_inactive=${includeInactive}`),
  memberGroupCreate: (body: { name: string; kind: 'category' | 'area' | 'list'; sort_order?: number }) =>
    request<MemberGroup>('/api/member-groups',
      { method: 'POST', body: JSON.stringify(body) }),
  memberGroupUpdate: (id: string, body: {
    name?: string; sort_order?: number; active?: boolean
  }) =>
    request<MemberGroup>(`/api/member-groups/${id}`,
      { method: 'PATCH', body: JSON.stringify(body) }),

  /** The roster import. Same three-step shape as the phone book's -- preview,
   *  confirm the mapping, apply over the same bytes. */
  memberImportPreview: (
    file: File, mapping?: Record<string, number>, hasHeaders?: boolean,
  ) => {
    const form = new FormData()
    form.append('file', file)
    if (mapping) form.append('mapping', JSON.stringify(mapping))
    if (hasHeaders !== undefined) form.append('has_headers', String(hasHeaders))
    return request<ImportPreview>('/api/members/import/preview', {
      method: 'POST', body: form,
    })
  },
  memberImportApply: (
    file: File, mapping: Record<string, number>, sha: string, hasHeaders: boolean,
  ) => {
    const form = new FormData()
    form.append('file', file)
    form.append('mapping', JSON.stringify(mapping))
    form.append('file_sha256', sha)
    form.append('has_headers', String(hasHeaders))
    return request<MemberImportResult>('/api/members/import', {
      method: 'POST', body: form,
    })
  },
  /** A fetch, not an `<a href>`: an expired session answers a link with a JSON
   *  401 and the browser NAVIGATES to it, throwing away a half-filled dialog. */
  memberImportTemplate: async (): Promise<Blob> => {
    const res = await fetch('/api/members/import/template', {
      credentials: 'same-origin',
    })
    if (!res.ok) throw new ApiError(res.status, 'Could not fetch the template.')
    return res.blob()
  },
  /** POST for a read, on purpose. A caller's number typed into the filter
   *  would otherwise land in the request URI, which Caddy writes to a rolled
   *  access file and uvicorn writes to stdout — two copies on disk, from the
   *  screen whose subject is people's phone numbers. */
  calls: (opts: { day?: string; missedOnly?: boolean; q?: string } = {}) =>
    request<CallDay>('/api/calls/search', {
      method: 'POST',
      body: JSON.stringify({
        day: opts.day ?? null,
        missed_only: !!opts.missedOnly,
        q: opts.q ?? '',
      }),
    }),
  /** Missed calls nobody has dealt with yet — for the banner on the dispatch
   *  screen. A later answered call from the same number clears it, so the
   *  count cannot only ever grow. */
  callsOutstanding: () =>
    request<{ outstanding: Array<{
      id: string; started_at: string; phone_e164: string | null
      dialled: string | null; contact_id: string | null; contact_name: string | null
    }> }>('/api/calls/missed'),
  callHandled: (id: string, handled: boolean, note?: string) =>
    request<{ id: string; handled_at: string | null }>(
      `/api/calls/${id}/handled`,
      { method: 'POST', body: JSON.stringify({ handled, note: note ?? null }) }),
  /** Same reason as `calls`: the search term is a phone number as often as
   *  not, and a URL is written to two access logs. */
  /** `limit` is the SERVER's cap made visible. It defaults to 100 and tops out
   *  at 500; the phone book asks for the maximum and says on screen when it has
   *  hit it, because a sheet somebody scrolls to the end of must not quietly be
   *  a first page. The default is left at 100 for the caller card, which is
   *  matching a number mid-call and does not want 500 rows. */
  /** AN EMPTY TERM GOES TO THE BROWSE ROUTE, and that is a permission
   *  decision rather than a tidy-up. `phonebook.view` is what draws the Phone
   *  book tab; `search.callers` is what the POST is gated on. Loading the
   *  sheet through the POST meant the screen was drawn by one key and filled
   *  by another, so a role with View and not Search opened its own tab onto
   *  "Searching callers is not permitted for your role." The server now reads
   *  a blank term as browsing and asks for `phonebook.view` either way; this
   *  keeps the two sides naming the same route for the same act.
   *
   *  No term is ever put in the URL -- see the note above. `limit` is not a
   *  search term. */
  contacts: (q = '', limit?: number) =>
    q.trim()
      ? request<Contact[]>('/api/contacts/search', {
          method: 'POST',
          body: JSON.stringify(limit === undefined ? { q } : { q, limit }),
        })
      : request<Contact[]>(
          limit === undefined ? '/api/contacts' : `/api/contacts?limit=${limit}`),
  contactLookup: (phone: string) =>
    request<{
      match: Contact | null
      incident_count: number
      /** Everyone this number belongs to. More than one means we cannot say
       *  whose house the person on the line is standing in, so the card says
       *  that instead of choosing. */
      holders: Array<{ id: string; display_name: string | null }>
      /** Which of the record's numbers rang: one saved as "Wife" is not him
       *  on the line (25 Oct 2026). */
      matched_number?: { label: string | null; is_primary: boolean; source?: string } | null
    }>(
      '/api/contacts/lookup',
      { method: 'POST', body: JSON.stringify({ phone }) }),
  contactHistory: (id: string) =>
    request<ContactHistory>(`/api/contacts/${id}/history`),
  contactCreate: (body: Record<string, unknown>) =>
    request<Contact>('/api/contacts', { method: 'POST', body: JSON.stringify(body) }),
  contactUpdate: (id: string, body: Record<string, unknown>) =>
    request<Contact>(`/api/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  contactDelete: (id: string) =>
    request<void>(`/api/contacts/${id}`, { method: 'DELETE' }),
  /** Put another number on somebody already in the book. `move` is required
   *  when the number is on a different caller — being told whose it is, and
   *  choosing, rather than it silently changing hands. */
  place: (id: string) => request<Place>(`/api/places/${id}`),
  mapSettings: () => request<MapSettings>('/api/map/settings'),

  /** The whole organization at a glance. No arguments — §24: "no
   *  configuration and no clicking". */
  dashboard: () => request<Dashboard>('/api/reports/dashboard'),

  /** Closed calls, newest first. Past calls only (`calls.past.review`); a POST
   *  because the term is a phone number as often as a name. `before` pages. */
  pastCalls: (body: {
    q?: string; date_from?: string | null; date_to?: string | null
    before?: string | null; before_id?: string | null; limit?: number
  }) =>
    request<{
      calls: {
        id: string; number: string; called_at: string; closed_at: string | null
        nature_label: string; caller_name: string | null
        resolution_label: string | null; priority: number
        location: string | null; responders: number
        /** Logged afterwards from paper; times corrected since (078). */
        logged_afterwards?: boolean; corrected?: boolean
        /** Logged with its came-in / closed time not known (083). */
        called_at_unknown?: boolean; closed_at_unknown?: boolean
        called_day?: string | null
      }[]
      more: boolean
    }>('/api/incidents/past', { method: 'POST', body: JSON.stringify(body) }),

  stock: () => request<Stock>('/api/equipment'),
  outNow: () => request<OutNow>('/api/equipment/out'),
  atPlace: (placeId: string) =>
    request<{ items: CollectionItem[] }>(`/api/equipment/at/${placeId}`),
  startRun: (body: { place_id: string; incident_id?: string | null }) =>
    request<{ id: string; items: number }>('/api/equipment/runs',
      { method: 'POST', body: JSON.stringify(body) }),
  /** `still_out` is the point of the whole flow, not an afterthought: the
   *  volunteer is not going to wait while somebody counts, so a missed item
   *  has to be noticed by the system rather than by memory. */
  completeRun: (runId: string, body: {
    picked_up: string[]; location_id?: string | null; member_id?: string | null
  }) =>
    request<RunResult>(`/api/equipment/runs/${runId}/complete`,
      { method: 'POST', body: JSON.stringify(body) }),
  moveItem: (itemId: string, body: { location_id: string; reason?: string | null }) =>
    request<{ id: string; location: string }>(
      `/api/equipment/items/${itemId}/move`,
      { method: 'POST', body: JSON.stringify(body) }),
  /** The server takes only these three. `out` and `in_transit` describe where
   *  an item IS and are written by releasing and returning it -- migration 031
   *  makes "in exactly one place" a CHECK rather than a comment, and a screen
   *  that could set them directly is a second writer of `state`. */
  setItemState: (itemId: string, body: {
    state: 'available' | 'repair' | 'lost'; location_id?: string | null
  }) =>
    request<{ id: string; state: string }>(
      `/api/equipment/items/${itemId}/state`,
      { method: 'POST', body: JSON.stringify(body) }),
  catalogue: () => request<Catalogue>('/api/equipment/catalogue'),
  release: (body: Record<string, unknown>) =>
    request<{ id: string; asset_tag: string; receipt_number: string | null }>(
      '/api/equipment/release',
      { method: 'POST', body: JSON.stringify(body) }),
  markReturned: (loanId: string, body: Record<string, unknown>) =>
    request<{ asset_tag: string; state: string; awaiting_depot_check: boolean }>(
      `/api/equipment/loans/${loanId}/return`,
      { method: 'POST', body: JSON.stringify(body) }),
  confirmArrival: (itemId: string, body: Record<string, unknown>) =>
    request<{ id: string; state: string }>(
      `/api/equipment/items/${itemId}/confirm`,
      { method: 'POST', body: JSON.stringify(body) }),
  addType: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/equipment/types',
      { method: 'POST', body: JSON.stringify(body) }),
  addLocation: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/equipment/locations',
      { method: 'POST', body: JSON.stringify(body) }),
  addItem: (body: Record<string, unknown>) =>
    request<{ id: string }>('/api/equipment/items',
      { method: 'POST', body: JSON.stringify(body) }),
  /** POST, like search: a GET would put the range and any filter in a URL that
   *  Caddy and uvicorn both write to disk. */
  reportIncidents: (body: Record<string, unknown>) =>
    request<ReportRows>('/api/reports/incidents', {
      method: 'POST', body: JSON.stringify(body),
    }),
  reportMembers: (body: Record<string, unknown>) =>
    request<MemberRows>('/api/reports/members', {
      method: 'POST', body: JSON.stringify(body),
    }),
  reportIncidentsCsv: (body: Record<string, unknown>) =>
    downloadCsv('/api/reports/incidents', body),
  reportMembersCsv: (body: Record<string, unknown>) =>
    downloadCsv('/api/reports/members', body),
  /** THE MESSAGES REPORT (5 Oct 2026): a month, a day, one broadcast. */
  messagesMonth: (month: string) =>
    request<MessagesMonth>(`/api/reports/messages?month=${encodeURIComponent(month)}`),
  messagesDay: (date: string) =>
    request<MessagesDay>(`/api/reports/messages/day?date=${encodeURIComponent(date)}`),
  messagesBroadcast: (id: string) =>
    request<BroadcastDetail>(`/api/reports/messages/broadcast/${id}`),

  // ---- group messages (066): texts and emails to whole groups -----------
  /** Who it would reach, before anything is sent. */
  groupReach: (body: GroupAudience) =>
    request<GroupReach>('/api/group-messages/reach',
      { method: 'POST', body: JSON.stringify(body) }),
  /** `expected_people` is the count the sender saw; the server refuses a
   *  different one. `request_key` makes a double press one message. */
  groupSend: (body: GroupAudience & {
    subject: string | null; body: string; expected_people: number; request_key: string
    /** Pictures uploaded through `messagingUploadPicture` (070). */
    picture_ids?: string[]
  }) =>
    request<{ id: string; repeat: boolean } & Partial<GroupReach>>('/api/group-messages',
      { method: 'POST', body: JSON.stringify(body) }),
  groupMessages: () => request<GroupMessageRow[]>('/api/group-messages'),
  groupMessage: (id: string) => request<BroadcastDetail>(`/api/group-messages/${id}`),
  // ---- scheduled group messages (067) --------------------------------
  scheduledList: () => request<ScheduledMessage[]>('/api/group-messages/scheduled'),
  /** `send_at` is a day and time on the organization's own clock,
   *  '2026-10-09T10:00'; the server turns it into an instant. */
  scheduledCreate: (body: GroupAudience & {
    subject: string | null; body: string; send_at: string; request_key: string
    picture_ids?: string[]
  }) =>
    request<ScheduledMessage>('/api/group-messages/scheduled',
      { method: 'POST', body: JSON.stringify(body) }),
  scheduledUpdate: (id: string, body: GroupAudience & {
    subject: string | null; body: string; send_at: string; picture_ids?: string[]
  }) =>
    request<ScheduledMessage>(`/api/group-messages/scheduled/${id}`,
      { method: 'PUT', body: JSON.stringify(body) }),
  scheduledCancel: (id: string) =>
    request<ScheduledMessage>(`/api/group-messages/scheduled/${id}/cancel`, { method: 'POST' }),
  groupMembers: (groupId: string) =>
    request<GroupMemberRow[]>(`/api/member-groups/${groupId}/members`),
  groupMemberAdd: (groupId: string, memberId: string) =>
    request<unknown>(`/api/member-groups/${groupId}/members`,
      { method: 'POST', body: JSON.stringify({ member_id: memberId }) }),
  groupMemberRemove: (groupId: string, memberId: string) =>
    request<unknown>(`/api/member-groups/${groupId}/members/${memberId}`,
      { method: 'DELETE' }),
  messagesCsv: (month: string) =>
    downloadCsv('/api/reports/messages/csv', { month }),

  /** One box across the whole system. Each group is gated by its own
   *  permission, and a group the role cannot see is not queried at all. */
  /** POST for a read, deliberately. A term in the URL is written to Caddy's
   *  rolled access file and uvicorn's stdout — two copies on disk of the
   *  phone number somebody just typed. The phone book and the call log both
   *  pay the same price for the same reason. */
  search: (q: string) =>
    request<SearchResults>('/api/search', {
      method: 'POST', body: JSON.stringify({ q }),
    }),

  /** Roles, who holds them, and the tree to tick against.
   *
   *  The tree comes from the SERVER, not from a copy in the front end. It
   *  mirrors what is actually enforced, so a node added to app/permissions.py
   *  appears here with no change on this side — and a checkbox can never exist
   *  for something no route checks. */
  roles: () => request<{
    roles: Role[]
    users: RoleHolder[]
    tree: PermissionBranch[]
    wildcard: string
  }>('/api/roles'),
  roleCreate: (body: Record<string, unknown>) =>
    request<Role>('/api/roles', { method: 'POST', body: JSON.stringify(body) }),
  roleUpdate: (id: string, body: Record<string, unknown>) =>
    request<Role>(`/api/roles/${id}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  roleDelete: (id: string) =>
    request<void>(`/api/roles/${id}`, { method: 'DELETE' }),
  /** REMOVED FROM THE ROLES SCREEN, kept only as API surface. Assigning a
   *  role is done on the Users section of the same tab, through
   *  `userUpdate`. Wiring this back into a second screen would give the
   *  product two places that decide who is an administrator. The route itself
   *  is live and `roles_test` drives it, including its lock-out guard. */
  roleAssign: (userId: string, roleId: string | null) =>
    request<{ id: string }>(`/api/roles/users/${userId}`, {
      method: 'PUT', body: JSON.stringify({ role_id: roleId }),
    }),

  /** Every vehicle, where it is, and who is driving it. Answers 200 even when
   *  the GPS platform is dark — see the Fleet type. */
  fleet: () => request<Fleet>('/api/fleet'),

  /** Texts that came IN. NOT an acceptance queue — the plan limits response to
   *  walkie-talkie or card. This is so a member's "on my way" is seen rather
   *  than landing nowhere, which is where it lands today. */
  inboundMessages: (unreadOnly = false) =>
    request<InboundMessage[]>(
      `/api/messages/inbound${unreadOnly ? '?unread_only=true' : ''}`,
    ),
  inboundMarkRead: (id: string) =>
    request<{ id: string }>(`/api/messages/inbound/${id}/read`, { method: 'POST' }),
  /** THE MESSAGING TAB (items 20-24, 23 Sep 2026). */
  conversations: (filter: ConversationFilter) =>
    request<Conversation[]>(`/api/messaging/conversations?filter=${filter}`),
  conversation: (key: string) =>
    request<ConversationThread>(`/api/messaging/conversations/${encodeURIComponent(key)}`),
  conversationRead: (key: string) =>
    request<{ marked: number }>(
      `/api/messaging/conversations/${encodeURIComponent(key)}/read`, { method: 'POST' }),
  conversationReply: (key: string, body: string, attachment_ids: string[] = []) =>
    request<OutboundReply>(
      `/api/messaging/conversations/${encodeURIComponent(key)}/reply`,
      { method: 'POST', body: JSON.stringify({ body, attachment_ids }) }),
  /** One picture to send (068), already shrunk by the browser to fit. */
  messagingUploadPicture: (picture: Blob) => {
    const form = new FormData()
    form.append('file', picture, picture.type === 'image/gif' ? 'picture.gif'
      : picture.type === 'image/png' ? 'picture.png' : 'picture.jpg')
    return request<{ id: string; mime: string; size: number }>(
      '/api/messaging/pictures', { method: 'POST', body: form })
  },
  /** The numbers one member or contact can be texted on: a label and the
   *  last four digits, never the whole number (6 Oct 2026). */
  messagingNumbers: (who: { member_id?: string; contact_id?: string }) =>
    request<NumberChoice[]>(`/api/messaging/numbers?${new URLSearchParams(
      who.member_id ? { member_id: who.member_id } : { contact_id: who.contact_id ?? '' })}`),
  /** Text somebody first: a member, a phone-book entry, or any number. */
  messagingStart: (body: {
    member_id?: string; contact_id?: string; phone_id?: string; number?: string; body: string
    attachment_ids?: string[]
  }) =>
    request<OutboundReply & { key: string }>('/api/messaging/start',
      { method: 'POST', body: JSON.stringify(body) }),
  /** After an unknown number was saved to the phone book: his texts follow him. */
  conversationAttach: (key: string, contactId: string) =>
    request<{ key: string }>(
      `/api/messaging/conversations/${encodeURIComponent(key)}/attach`,
      { method: 'POST', body: JSON.stringify({ contact_id: contactId }) }),
  messagingUnread: () => request<{ unread: number; scoped?: boolean }>('/api/messaging/unread'),
  /** The picture or the voice note itself; the session cookie carries the
   *  right to see it, so an <img> or an <audio> can point straight at it. */
  mediaUrl: (id: string) => `/api/messaging/media/${id}`,
  /** THE FEEDBACK BOARD (065): report a bug, request a feature, follow it. */
  feedbackList: () =>
    request<{ view_all: boolean; items: FeedbackItem[] }>('/api/feedback'),
  feedbackUnread: () => request<{ unread: number }>('/api/feedback/unread'),
  feedbackCreate: (body: {
    kind: FeedbackKind; title: string; body: string | null; screen: string | null
  }) =>
    request<FeedbackItem>('/api/feedback', { method: 'POST', body: JSON.stringify(body) }),
  feedbackRead: (id: string) => request<FeedbackDetail>(`/api/feedback/${id}`),
  feedbackReply: (id: string, body: string) =>
    request<FeedbackDetail>(`/api/feedback/${id}/reply`, {
      method: 'POST', body: JSON.stringify({ body }),
    }),
  /** One screenshot. FormData, so `request` leaves the boundary to the browser. */
  feedbackAttach: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<FeedbackAttachment>(`/api/feedback/${id}/attachments`, {
      method: 'POST', body: form,
    })
  },
  feedbackPictureUrl: (id: string) => `/api/feedback/attachments/${id}`,
  /** Ask the map where an address is. A BUTTON, never something that fires
   *  while the address is being typed: OpenStreetMap's usage policy calls
   *  autocomplete out by name as a way to get an application blocked.
   *
   *  Resolves even when nothing was found — "the map does not know this
   *  address" is an ordinary answer a dispatcher acts on by asking for a cross
   *  street, not an error. */
  placeLocate: (id: string, force = false) =>
    request<{ located: boolean; detail: string | null; place: Place }>(
      `/api/places/${id}/locate${force ? '?force=true' : ''}`,
      { method: 'POST' },
    ),
  placeUpdate: (id: string, body: {
    entry_codes?: string | null
    cross_street_a?: string | null
    cross_street_b?: string | null
  }) =>
    request<Place>(`/api/places/${id}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  siteNoteAdd: (placeId: string, body: string, severity: string) =>
    request<Place>(`/api/places/${placeId}/notes`, {
      method: 'POST', body: JSON.stringify({ body, severity }),
    }),
  /** Replace without erasing. The old note keeps its date and author and stops
   *  being current — "the code was 4402 until 2026" and "the code has always
   *  been 8891" are different facts. */
  siteNoteSupersede: (placeId: string, noteId: string, body: string, severity: string) =>
    request<Place>(`/api/places/${placeId}/notes/${noteId}/supersede`, {
      method: 'POST', body: JSON.stringify({ body, severity }),
    }),
  siteNoteRetire: (placeId: string, noteId: string) =>
    request<Place>(`/api/places/${placeId}/notes/${noteId}/retire`, {
      method: 'POST',
    }),
  /** Says what the file WOULD do. Writes nothing. */
  contactImportPreview: (
    file: File,
    mapping?: Record<string, number>,
    hasHeaders?: boolean,
  ) => {
    const form = new FormData()
    form.append('file', file)
    if (mapping) form.append('mapping', JSON.stringify(mapping))
    if (hasHeaders !== undefined) form.append('has_headers', String(hasHeaders))
    return request<ImportPreview>('/api/contacts/import/preview', {
      method: 'POST', body: form,
    })
  },
  /** Does it. `sha` is the digest the preview returned: the same file is
   *  uploaded again rather than parked on the server, and this is what makes
   *  "the same file" a fact instead of an assumption. */
  contactImportApply: (
    file: File,
    mapping: Record<string, number>,
    sha: string,
    hasHeaders: boolean,
  ) => {
    const form = new FormData()
    form.append('file', file)
    form.append('mapping', JSON.stringify(mapping))
    form.append('file_sha256', sha)
    form.append('has_headers', String(hasHeaders))
    return request<ImportResult>('/api/contacts/import', {
      method: 'POST', body: form,
    })
  },
  /** The example file. A fetch rather than a plain `<a href>`: an expired
   *  session would answer the link with a JSON 401 and the browser would
   *  NAVIGATE to it, throwing away a half-filled import dialog to show a page
   *  about something the person pressing it has never heard of. This keeps the
   *  failure inside the dialog, where it can be a sentence. */
  contactImportTemplate: async (): Promise<Blob> => {
    const res = await fetch('/api/contacts/import/template', {
      credentials: 'same-origin',
    })
    if (!res.ok) {
      throw new ApiError(res.status, 'Could not fetch the template.')
    }
    return res.blob()
  },
  contactImportBatches: () =>
    request<ImportBatch[]>('/api/contacts/import'),
  contactImportBatch: (batchId: string) =>
    request<ImportBatch>(`/api/contacts/import/${batchId}`),
  /** Takes a whole import back. Nothing is restored over a value somebody has
   *  changed since; what it leaves alone comes back in `left_alone`. */
  contactImportUndo: (batchId: string) =>
    request<ImportUndone>(`/api/contacts/import/${batchId}/undo`, {
      method: 'POST',
    }),
  contactAddPhone: (
    id: string,
    body: { phone: string; label?: string | null; make_primary?: boolean; move?: boolean },
  ) =>
    request<Contact>(`/api/contacts/${id}/phones`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  /** Answers with the updated contact, like `contactAddPhone` -- removing the
   *  primary number changes the derived `phone_e164`, and working that out in
   *  the browser would be a second definition of "primary". */
  contactRemovePhone: (id: string, phoneId: string) =>
    request<Contact>(`/api/contacts/${id}/phones/${phoneId}`, { method: 'DELETE' }),
  settingsList: (list: ListName, includeRetired = false) =>
    request<ListItem[]>(`/api/settings/${list}?include_retired=${includeRetired}`),
  settingsCreate: (list: ListName, body: Partial<ListItem>) =>
    request<ListItem>(`/api/settings/${list}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  settingsUpdate: (list: ListName, id: string, body: Record<string, unknown>) =>
    request<ListItem>(`/api/settings/${list}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // -------------------------------------------------------------------------
  // The conditional question tree
  //
  // ITS OWN PREFIX, `/api/questions`, AND NOT A FIFTH SETTINGS LIST.
  // `/api/settings/{list_name}` validates that segment against a whitelist of
  // four tables, so a sibling at `/api/settings/question-sets` would be matched
  // by it first and answered 422 — a dead route nothing could see, because the
  // two paths are different strings.
  //
  // EVERY EDITOR CALL NEEDS `settings.lists`, WHICH IS NOT A NEW LEAF. It
  // already means "the organization's own lists" and already gates the natures
  // these hang off; PLAN.html section 19 forbids a tick for a distinction the
  // product draws nowhere. The two reads a CALL needs — the counts and one
  // nature's tree — are ungated, exactly like the nature buttons and the
  // caller-ID pop: gating them leaves a dispatcher looking at a nature he
  // cannot ask the questions for, mid-emergency.
  // -------------------------------------------------------------------------

  /** The reusable bundles, with the two counts somebody wants before they
   *  press Retire. */
  questionSets: (includeRetired = false) =>
    request<QuestionSet[]>(
      `/api/questions/sets?include_retired=${includeRetired}`),
  questionSetCreate: (body: { name: string; sort_order?: number }) =>
    request<QuestionSet>('/api/questions/sets',
      { method: 'POST', body: JSON.stringify(body) }),
  /** One set, its questions in its own order, and every nature asking it. */
  questionSet: (setId: string, includeRetired = false) =>
    request<QuestionSetDetail>(
      `/api/questions/sets/${setId}?include_retired=${includeRetired}`),
  /** Rename it, move it, RETIRE it. There is no delete route and there must not
   *  be one: every answer already recorded under one of its questions has to
   *  keep reading correctly. */
  questionSetUpdate: (setId: string, body: {
    name?: string; sort_order?: number; retired?: boolean
  }) =>
    request<QuestionSet>(`/api/questions/sets/${setId}`,
      { method: 'PATCH', body: JSON.stringify(body) }),
  questionSetAdd: (setId: string, body: QuestionInput) =>
    request<CallQuestion>(`/api/questions/sets/${setId}/questions`,
      { method: 'POST', body: JSON.stringify(body) }),
  /** The FULL list in its new order. A real reorder — no row is deleted and no
   *  id changes, which matters because a question's id is what every stored
   *  answer points at — and the server refuses a list that is not exactly what
   *  it holds, so a screen opened before somebody else added a question is told
   *  to reload rather than writing positions for the rows it happens to know. */
  questionSetOrder: (setId: string, ids: string[]) =>
    request<CallQuestion[]>(`/api/questions/sets/${setId}/order`,
      { method: 'POST', body: JSON.stringify({ ids }) }),

  /** How many questions each nature asks. UNGATED — it is read when the intake
   *  form opens, beside the natures themselves. */
  questionNatureCounts: () =>
    request<NatureQuestionCount[]>('/api/questions/natures'),
  /** One nature's resolved tree. UNGATED, and this is what the intake form and
   *  the working call run the conditional questions from. It carries the WHOLE
   *  tree rather than only what is visible, because visibility depends on
   *  answers nobody has given yet. */
  questionTree: (natureId: string) =>
    request<{ nature_id: string; questions: CallQuestion[] }>(
      `/api/questions/natures/${natureId}/tree`),

  questionNature: (natureId: string) =>
    request<NatureQuestions>(`/api/questions/natures/${natureId}`),

  /** Every make in the vehicle catalogue, and the colours offered. UNGATED --
   *  the vehicle question is asked while taking a call. Empty lists before the
   *  catalogue is loaded, which leaves the boxes taking typing. */
  vehicleCatalogue: () =>
    request<{
      makes: string[]
      colours: string[]
      /** Every province and territory, and every state, A–Z, by country. */
      plate_regions: Record<string, string[]>
      /** The few offered above the full list, by country. */
      plate_top: Record<string, string[]>
    }>('/api/vehicles'),
  /** One make's models, each with the years it was sold in, newest first. The
   *  make is matched without regard to case, and an unknown one is an empty
   *  list rather than an error. */
  vehicleModels: (make: string) =>
    request<{ make: string; models: { model: string; years: number[] }[] }>(
      `/api/vehicles/models?make=${encodeURIComponent(make)}`),
  /** What the broadcast would say. WRITES NOTHING — no incident, no answers,
   *  no row anywhere; the server reads the nature and its tree and composes in
   *  memory.
   *
   *  IT SENDS THE ANSWERS AND NOT WHICH OF THEM GO OUT. The server reads
   *  `in_broadcast` off each question and runs the same visibility rule the
   *  dispatcher's form runs, so an answer to a question the tree does not
   *  bring up cannot reach the text.
   *
   *  A POST FOR A READ, like the three search routes: the answers are a map,
   *  and a map in a query string is written to Caddy's access file and to
   *  uvicorn's stdout on every press. */
  questionBroadcastPreview: (natureId: string, answers: Record<string, string>,
                             callerLeaving = false) =>
    request<BroadcastPreview>(
      `/api/questions/natures/${natureId}/broadcast-preview`,
      { method: 'POST', body: JSON.stringify({ answers, caller_leaving: callerLeaving }) }),
  /** A question only this nature asks, appended to the end of its list. The
   *  item row that puts it in the sequence is written in the same transaction —
   *  a question owned by a nature whose list does not mention it is invisible
   *  and reachable by nothing. */
  questionNatureAdd: (natureId: string, body: QuestionInput) =>
    request<{ question: CallQuestion } & NatureQuestions>(
      `/api/questions/natures/${natureId}/questions`,
      { method: 'POST', body: JSON.stringify(body) }),
  questionNatureAddSet: (natureId: string, setId: string) =>
    request<{ item: { id: string; set_id: string; name: string } } & NatureQuestions>(
      `/api/questions/natures/${natureId}/sets`,
      { method: 'POST', body: JSON.stringify({ set_id: setId }) }),
  /** THE TWO CASES ARE NOT THE SAME ACT and the answer says which happened. A
   *  SET is shared, so it comes off this nature and stays on every other one. A
   *  QUESTION THIS NATURE OWNS has nowhere else to live, so removing it RETIRES
   *  it — kept, never deleted, so every answer recorded under its key still
   *  reads correctly and no later question can inherit their meaning. */
  questionNatureRemove: (natureId: string, itemId: string) =>
    request<{
      removed: { question_retired: string | null; set_removed: string | null }
    } & NatureQuestions>(
      `/api/questions/natures/${natureId}/items/${itemId}`, { method: 'DELETE' }),
  questionNatureOrder: (natureId: string, ids: string[]) =>
    request<NatureQuestions>(`/api/questions/natures/${natureId}/order`,
      { method: 'POST', body: JSON.stringify({ ids }) }),

  /** Reword it, change its options or its condition, retire it, restore it.
   *  ONE ROUTE FOR BOTH HOMES — a question in a shared set and one owned by a
   *  nature differ in where they are LISTED and in nothing about how they are
   *  edited. */
  questionUpdate: (questionId: string, body: QuestionPatch) =>
    request<CallQuestion>(`/api/questions/${questionId}`,
      { method: 'PATCH', body: JSON.stringify(body) }),
  status: () => request<Status>('/api/status'),
  whoami: () => request<Principal>('/api/session'),
  signIn: (login: string, password: string) =>
    request<Principal>('/api/session', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
    }),
  signOut: () => request<void>('/api/session', { method: 'DELETE' }),
  /** Somebody is using this screen (075): the sign-in now ends the
   *  organization's idle time from now. Real use only. */
  sessionTouch: () => request<{ expires_at: string; now?: string }>('/api/session/touch', { method: 'POST' }),
  /** When this sign-in ends if nobody uses the screen again. */
  sessionExpiry: () => request<{ expires_at: string | null; now?: string }>('/api/session/expiry'),
  /** His own password: the current one first (10 Oct 2026). */
  changePassword: (current: string, next: string) =>
    request<void>('/api/session/password', {
      method: 'POST', body: JSON.stringify({ current, new: next }),
    }),
}


// ---------------------------------------------------------------------------
// Super admin -- a different door. Its own cookie, its own routes, no
// organization scoping anywhere.
// ---------------------------------------------------------------------------

export type AdminPrincipal = {
  login: string
  display_name: string
  /** False when SECRET_KEY is unset, in which case no credential can be
   *  stored at all and the console says so rather than failing on save. */
  secrets_available: boolean
}

/** The admin view of an organization: carries the id, because the super admin
 *  addresses organizations directly. The tenant-facing `Organization` above
 *  deliberately does not -- a dispatcher never names one. */
export type AdminOrganization = {
  /** ISO 3166-1 alpha-2. Decides how a number typed without a
   *  leading + is read for this customer. */
  default_region?: string
  id: string
  slug: string
  name: string
  timezone: string
  active: boolean
  dispatch_tenant_id: string | null
  fleet_network: string | null
}

export type AdminUser = {
  id: string
  login: string
  display_name: string
  role_name: string | null
  disabled: boolean
  /** The seat this dispatcher takes on sign-in. The air hears the seat name,
   *  never theirs. */
  dispatch_connection_id: string | null
  connection_label: string | null
  fleet_account_ready: boolean
  email: string | null
  /** Whether the CRM currently grants them a GPS platform login. Traccar is
   *  reconciled to match it. */
  traccar_access: boolean
  /** Traccar's own id, kept even after a revoke so granting again reuses the
   *  account rather than making a second one for the same person. */
  traccar_user_id: number | null
  traccar_email: string | null
  last_seen_at: string | null
}

export type AdminRole = { id: string; name: string; dispatch_level: number }

export type AdminConnection = {
  id: string
  label: string
  dispatch_id: string | null
  dispatch_level: number
  fleet_account_ready: boolean
  fleet_login: string | null
  sort_order: number
  active: boolean
  assigned_people: number
}

/** Where a connection stands, in the same four facts for every kind.
 *
 *  `configured` and `enabled` are separate on purpose. Credentials stored with
 *  the switch off is a decision somebody made; no credentials at all was never
 *  set up. Collapsing the two into "off" is what made a fully wired customer's
 *  console look untouched. */
export type ConnectionStatus = {
  state: 'live' | 'off' | 'blocked' | 'error' | 'unconfigured'
  configured: boolean
  enabled: boolean
  checked_at: string | null
  ok: boolean | null
  detail: string | null
}

/** THE FEEDBACK BOARD (migration 065, 5 Oct 2026): a bug report or a feature
 *  request, and what became of it. A user is sent only his own; whoever may
 *  see everything is sent every report from his organization. */
export type FeedbackKind = 'bug' | 'feature'
export type FeedbackStatus =
  | 'new' | 'need_info' | 'accepted' | 'in_progress' | 'done' | 'declined'
export type FeedbackItem = {
  id: string
  number: number
  kind: FeedbackKind
  title: string
  body: string
  status: FeedbackStatus
  status_at: string
  reported_by_label: string | null
  /** This login sent it. */
  mine: boolean
  /** Advance Talk entered it for the organization. */
  entered_by_operator: boolean
  screen: string | null
  /** A reply or a new status this login has not read. */
  unread: boolean
  created_at: string
  last_activity_at: string
  attachment_count: number
}
export type FeedbackEvent = {
  id: string
  /** 'note' is the operator's private note; the customer's side is never
   *  sent one. */
  kind: 'message' | 'status' | 'note'
  from_operator: boolean
  author_label: string | null
  body: string | null
  status_to: FeedbackStatus | null
  created_at: string
}
export type FeedbackAttachment = { id: string; mime: string; size: number; created_at: string }
export type FeedbackDetail = FeedbackItem & {
  events: FeedbackEvent[]
  attachments: FeedbackAttachment[]
}
/** The same report as the super admin console is handed it. */
export type AdminFeedbackItem = {
  id: string
  organization_id: string
  organization_slug: string
  organization_name: string
  number: number
  kind: FeedbackKind
  title: string
  body: string
  status: FeedbackStatus
  status_at: string
  reported_by_label: string | null
  entered_by_operator: boolean
  screen: string | null
  /** Something the operator has not read. */
  unread: boolean
  /** Something the customer has not read. */
  customer_unread: boolean
  created_at: string
  last_activity_at: string
  attachment_count: number
}
export type AdminFeedbackDetail = AdminFeedbackItem & {
  events: FeedbackEvent[]
  attachments: FeedbackAttachment[]
}

/** THE MESSAGES REPORT (5 Oct 2026). Counts of what became of texts and
 *  emails: `accepted` is what a provider took, never "delivered" unless the
 *  provider said so; `carrier_errors` and `bounced` are their words back. */
export type MessageCounts = {
  total: number
  accepted: number
  delivered: number
  carrier_errors?: number
  bounced?: number
  failed: number
  /** Still on its way out; not accepted yet. */
  queued: number
}
export type MessagesMonth = {
  year: number
  month: number
  timezone: string
  sms: MessageCounts
  email: MessageCounts
  broadcasts: number
  calls: number
  replies: number
  days: {
    day: string
    sms: MessageCounts | null
    email: MessageCounts | null
    broadcasts: number
    calls: number
    replies: number
  }[]
}
export type MessageLine = {
  line: 'broadcast' | 'reply'
  id: string
  at: string
  kind: string
  incident_number?: string | null
  nature_label?: string | null
  sent_by_label: string | null
  recipient_count?: number
  audience?: string[] | null
  sms?: MessageCounts | null
  email?: MessageCounts | null
  to_label?: string
  outcome?: string
  reason?: string | null
}
export type MessagesDay = { day: string; timezone: string; lines: MessageLine[] }
export type BroadcastDetail = {
  id: string
  kind: string
  at: string
  /** Null for a group message (066), which is about no call. */
  incident_id: string | null
  incident_number: string | null
  /** A group message's own facts (066); null / empty on a call's broadcast. */
  subject?: string | null
  send_by?: 'sms' | 'email' | 'both' | null
  unreached?: string[]
  /** A group message's pictures (070), by id. */
  pictures?: { id: string; mime: string; size: number }[]
  nature_label: string | null
  sent_by_label: string | null
  recipient_count: number
  audience: string[] | null
  /** Null when withheld: a `site` broadcast is a gate code (shown only
   *  with the entry-codes tick); a `callback` body is the caller's number
   *  (shown to nobody in a report). */
  body: string | null
  body_withheld: boolean
  withheld_reason: 'entry_codes' | 'caller_number' | null
  deliveries: {
    id: string
    member_label: string | null
    callsign: string | null
    channel: string
    /** Which part of a long text (081); null when it went whole. */
    part?: number | null
    parts?: number | null
    status: string
    outcome: string
    reason: string | null
    provider_status: string | null
    provider_ref: string | null
    queued_at: string
    sent_at: string | null
    delivered_at: string | null
    status_at: string | null
  }[]
}

export const adminApi = {
  /** The dispatch SEATS -- "Dispatcher 1", "Dispatcher 2".
   *
   *  A different thing from `connections()` below, which is the six integration
   *  credentials. They used to share a URL, and the first one registered
   *  answered both. */
  dispatchConnections: (orgId: string) =>
    request<{ max_connections: number; connections: AdminConnection[] }>(
      `/api/admin/organizations/${orgId}/dispatch-connections`),
  seatCreate: (orgId: string, body: Record<string, unknown>) =>
    request<AdminConnection>(
      `/api/admin/organizations/${orgId}/dispatch-connections`,
      { method: 'POST', body: JSON.stringify(body) }),
  seatUpdate: (orgId: string, id: string, body: Record<string, unknown>) =>
    request<AdminConnection>(
      `/api/admin/organizations/${orgId}/dispatch-connections/${id}`,
      { method: 'PATCH', body: JSON.stringify(body) }),
  seatProvision: (orgId: string, id: string) =>
    request<Record<string, unknown>>(
      `/api/admin/organizations/${orgId}/dispatch-connections/${id}/provision`,
      { method: 'POST' }),
  seatRefresh: (orgId: string, id: string) =>
    request<Record<string, unknown>>(
      `/api/admin/organizations/${orgId}/dispatch-connections/${id}/refresh`,
      { method: 'POST' }),
  organizationUpdate: (id: string, body: Record<string, unknown>) =>
    request<AdminOrganization>(`/api/admin/organizations/${id}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  users: (orgId: string) =>
    request<{ users: AdminUser[]; roles: AdminRole[] }>(
      `/api/admin/organizations/${orgId}/users`),
  userCreate: (orgId: string, body: Record<string, unknown>) =>
    request<{ id: string }>(`/api/admin/organizations/${orgId}/users`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  // Removed: provision / fleetCredentials / refreshDispatch on a USER. They
  // are leftovers from the per-user model that the per-seat model replaced, and
  // the routes they called no longer exist. Harmless-looking dead code, except
  // that their return types read as a live contract with the provisioning
  // server -- and calling one would have got index.html and a silent null back,
  // not a 404. Seats are provisioned through seatProvision/seatRefresh.
  userUpdate: (orgId: string, userId: string, body: Record<string, unknown>) =>
    request<{ id: string; warning?: string }>(
      `/api/admin/organizations/${orgId}/users/${userId}`, {
        method: 'PATCH', body: JSON.stringify(body),
      }),

  /** Grant or revoke one person's login to the GPS platform.
   *
   *  On a grant that CREATES an account, `password` comes back once and is
   *  never stored — the same shape as the dispatch panel's signing key. On a
   *  re-grant of an existing account it is null, because the old password
   *  still works and nothing here knows it. */
  userGpsAccess: (orgId: string, userId: string, granted: boolean) =>
    request<{
      granted: boolean
      traccar_user_id?: number
      email?: string
      password: string | null
      detail: string
      remote_failed?: boolean
    }>(`/api/admin/organizations/${orgId}/users/${userId}/gps-access`, {
      method: 'PUT', body: JSON.stringify({ granted }),
    }),
  whoami: () => request<AdminPrincipal>('/api/admin/session'),
  signIn: (login: string, password: string) =>
    request<AdminPrincipal>('/api/admin/session', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
    }),
  signOut: () => request<void>('/api/admin/session', { method: 'DELETE' }),
  organizations: () => request<AdminOrganization[]>('/api/admin/organizations'),
  organizationCreate: (body: Record<string, unknown>) =>
    request<{ id: string; slug: string; name: string; url: string }>(
      '/api/admin/organizations',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  connections: (orgId: string) =>
    request<Record<string, any>>(`/api/admin/organizations/${orgId}/connections`),
  connectionSave: (orgId: string, kind: string, body: Record<string, unknown>) =>
    request<Record<string, any>>(
      `/api/admin/organizations/${orgId}/connections/${kind}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  connectionTest: (orgId: string, kind: string) =>
    request<{ ok: boolean; detail: string }>(
      `/api/admin/organizations/${orgId}/connections/${kind}/test`,
      { method: 'POST' },
    ),
  /** Forget a connection entirely: credentials cleared, switch off. Saving a
   *  blank field cannot do this -- blank means "keep what is stored" -- so
   *  letting go of a third-party server needs its own verb. */
  connectionRevoke: (orgId: string, kind: string) =>
    request<Record<string, any>>(
      `/api/admin/organizations/${orgId}/connections/${kind}`,
      { method: 'DELETE' },
    ),

  /** THE FEEDBACK BOARD (065), every customer's reports. */
  feedback: () =>
    request<{ items: AdminFeedbackItem[]; counts: Record<string, number>; unread: number }>(
      '/api/admin/feedback'),
  feedbackUnread: () => request<{ unread: number }>('/api/admin/feedback/unread'),
  feedbackSettings: () =>
    request<{ notify_email: string | null }>('/api/admin/feedback/settings'),
  feedbackSettingsSave: (notifyEmail: string | null) =>
    request<{ notify_email: string | null }>('/api/admin/feedback/settings', {
      method: 'PUT', body: JSON.stringify({ notify_email: notifyEmail }),
    }),
  feedbackCreate: (orgId: string, body: {
    kind: FeedbackKind; title: string; body: string | null
    status: FeedbackStatus; reported_by_label: string | null
  }) =>
    request<AdminFeedbackDetail>(`/api/admin/feedback/${orgId}`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  feedbackRead: (orgId: string, id: string) =>
    request<AdminFeedbackDetail>(`/api/admin/feedback/${orgId}/${id}`),
  feedbackStatus: (orgId: string, id: string, status: FeedbackStatus) =>
    request<AdminFeedbackDetail>(`/api/admin/feedback/${orgId}/${id}/status`, {
      method: 'POST', body: JSON.stringify({ status }),
    }),
  /** `isPrivate` keeps it as a note the customer is never sent. */
  feedbackReply: (orgId: string, id: string, body: string, isPrivate: boolean) =>
    request<AdminFeedbackDetail>(`/api/admin/feedback/${orgId}/${id}/reply`, {
      method: 'POST', body: JSON.stringify({ body, private: isPrivate }),
    }),
  feedbackPictureUrl: (orgId: string, id: string) =>
    `/api/admin/feedback/${orgId}/attachments/${id}`,

  /** The platform's own Google keys (migration 056), above every customer.
   *  Whether each is set, never what it is -- the browser key included. */
  platformSettings: () => request<PlatformSettings>('/api/admin/platform-settings'),
  /** Replace a key by sending it, or forget one by naming it in `clear`. A
   *  blank means "keep what is stored", so clearing needs its own words. */
  platformSettingsSave: (body: {
    google_server_key?: string
    google_browser_key?: string
    clear?: ('google_server_key' | 'google_browser_key')[]
  }) =>
    request<PlatformSettings>('/api/admin/platform-settings', {
      method: 'PUT', body: JSON.stringify(body),
    }),
  /** One Geocoding lookup and one suggestion, with the stored SERVER key. The
   *  browser key is tried in this browser instead (`basemap.testBrowserKey`). */
  platformSettingsTest: () =>
    request<{ ok: boolean; detail: string }>('/api/admin/platform-settings/test', {
      method: 'POST',
    }),
}

/** What the super admin console is told about the platform's Google keys. */
export type PlatformSettings = {
  has_google_server_key: boolean
  has_google_browser_key: boolean
  last_test_at: string | null
  last_test_ok: boolean | null
  last_test_detail: string | null
  updated_at: string | null
  updated_by: string | null
  secrets_available: boolean
}

// ---- group messages (066) --------------------------------------------------
export type GroupAudience = {
  group_ids: string[]
  /** Every active member, instead of groups. */
  everyone: boolean
  send_by: 'sms' | 'email' | 'both'
}
export type GroupReach = {
  audience: string[]
  /** Active members in the chosen groups, reachable or not. */
  in_groups: number
  /** How many get it at all, and how. */
  people: number
  by_text: number
  by_email: number
  /** Texts and emails that will go (a member with two numbers gets two). */
  texts: number
  emails: number
  /** Names or units of the ones who cannot be reached that way. Never a number. */
  unreached: string[]
}
export type GroupMessageRow = {
  id: string
  sent_at: string
  body: string
  subject: string | null
  send_by: 'sms' | 'email' | 'both'
  audience: string[]
  recipient_count: number
  unreached: number
  sent_by_label: string | null
  queued: number
  failed: number
  reached: number
}
export type GroupMemberRow = {
  id: string
  callsign: string | null
  label: string | null
  by_text: boolean
  by_email: boolean
}
export type ScheduledMessage = {
  id: string
  send_at: string
  group_ids: string[]
  everyone: boolean
  send_by: 'sms' | 'email' | 'both'
  subject: string | null
  body: string
  /** 'sending' only for the half minute it is going. */
  status: 'scheduled' | 'sending' | 'sent' | 'cancelled' | 'failed'
  broadcast_id: string | null
  attempts: number
  /** Why it did not go, in words. */
  last_error: string | null
  created_by_label: string
  created_at: string
  updated_at: string
  updated_by_label: string | null
  cancelled_at: string | null
  cancelled_by_label: string | null
  sent_at: string | null
  /** The groups by name as they are now; a retired one says so. */
  group_names: string[]
  /** Who it is signed by: whoever last changed it. */
  signed_by_label: string
  /** Its pictures (070), kept with it until it goes. */
  pictures: { id: string; mime: string; size: number }[]
}
export type NumberChoice = {
  id: string
  label: string | null
  /** Marked as his main number. */
  main: boolean
  /** Ticked for broadcast texts (members only). */
  texts: boolean
  last4: string
  /** What is chosen unless he picks another: the main number. */
  default: boolean
}
